import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { usePageView } from "@/hooks/usePageView";
import { trackEvent } from "@/lib/engagement";
import { z } from "zod/v4";
import { toast } from "@/lib/toast";
import { exportPapersToCSV } from "@/lib/csv-export";
import { exportPapersToBibTeX } from "@/lib/bibtex-export";
import { PaperTable } from "@/components/papers/PaperTable";
import { RecentlyAddedChip } from "@/components/papers/RecentlyAddedChip";
import { PaperDetail } from "@/components/expand/PaperDetail";
import { ActionBar } from "@/components/expand/ActionBar";
import { SelectionBar } from "@/components/expand/SelectionBar";
import { FilterBar } from "@/components/expand/FilterBar";
import { OperationBar, OperationBarCallout } from "@/components/expand/operation-bar";
import { usePapers, usePromotePapers, useScoreOverride, useUpdatePaper } from "@/hooks/usePapers";
import { StartOverDialog } from "@/components/concepts/StartOverDialog";
import { useUndoReset } from "@/hooks/useConceptReset";
import { apiErrorDetail } from "@/lib/api-error";
import { useTrawl } from "@/hooks/useTrawl";
import { useCycleSignals } from "@/hooks/useCycleSignals";
import { useSnowball } from "@/hooks/useSnowball";

import { useScoring } from "@/hooks/useScoring";
import { useBibtexImport } from "@/hooks/useBibtexImport";
import { usePdfRetrieval } from "@/hooks/usePdfRetrieval";
import { usePdfUpload } from "@/hooks/usePdfUpload";
import { useAddExcerpt } from "@/hooks/useExcerpts";
import { useUpdateConcept, useConcept, useConfirmDraftedQuestion } from "@/hooks/useConcepts";
import { usePapersCount } from "@/hooks/usePapersCount";
import { ConceptPopover } from "@/components/concepts/ConceptPopover";
import { DraftedQuestionCallout } from "@/components/expand/DraftedQuestionCallout";
import { useCurrentUser, isPaidPlan } from "@/hooks/useCurrentUser";
import {
  useFilteredPapers,
  applyPaperIdScope,
  type PaperFilters,
  type SortField,
  type SortDirection,
  type AngleContext,
} from "@/hooks/useFilteredPapers";
import { useAngleRescoreRefresh, useAngles } from "@/hooks/useFacets";
import { AngleManager } from "@/components/angles/AngleManager";
import { validAngle } from "@/lib/angles";
import { computeCullSet, cullFilters, describeFilters } from "@/lib/cullOthers";
import { useSelectionStore } from "@/stores/selection-store";
import { useTrawlStore } from "@/stores/trawl-store";
import { deriveRecentlyAddedIds, useRecentlyAddedStore } from "@/stores/recently-added-store";
import { useScoringStore } from "@/stores/scoring-store";
import { useBackgroundScoringPending } from "@/hooks/useScoringBatchStatus";
import { useSnowballStore } from "@/stores/snowball-store";
import { useZoteroBulkImportModalStore } from "@/stores/zotero-bulk-import-modal-store";

import { usePdfStore } from "@/stores/pdf-store";
import { useFilterStore } from "@/stores/filter-store";
import { useAddPapers } from "@/hooks/usePapers";
import type { PaperWithRole } from "@/types/paper";
import { ArrowLeft, Minimize2, X } from "lucide-react";
import { PdfViewer } from "@/components/expand/PdfViewer";
import { HighlightsSidebar } from "@/components/expand/HighlightsSidebar";
import { FullTextViewer } from "@/components/expand/FullTextViewer";
import { ExplanationCard } from "@/components/expand/ExplanationCard";

import { useSummarizePaper, useExplainSelection } from "@/hooks/usePaperSummary";
import { useHighlights, insertHighlightInCache, type PaperHighlight } from "@/hooks/useHighlights";
import { useSmartTrawl } from "@/hooks/useSmartTrawl";
import { useAnnotationImport } from "@/hooks/useAnnotationImport";
import { useReadingStatus } from "@/hooks/useReadingStatus";
import { useReadingAutopromote } from "@/hooks/useReadingAutopromote";
import { shouldArrowNavigatePapers } from "@/lib/arrowPaperNav";
import { isReviewed } from "@/lib/reviewed";
import { useNotes } from "@/hooks/useNotes";
import { useTags, useTagActions, useUpdatePaperTags } from "@/hooks/useTags";
import { BulkPdfImportDialog } from "@/components/expand/BulkPdfImportDialog";
import { BulkDismissDialog } from "@/components/expand/BulkDismissDialog";
import { CullOthersDialog } from "@/components/expand/CullOthersDialog";
import { SimilarPapersModal } from "@/components/expand/SimilarPapersModal";
import { useMonitorSettings, useMonitorRun, useTriagePaper, useTriageBatch } from "@/hooks/useMonitor";
import { MonitorInbox } from "@/components/expand/MonitorInbox";
import { useMonitorStore } from "@/stores/monitor-store";
import { useFilterPresets, useFilterPresetActions } from "@/hooks/useFilterPresets";
import { useClaims } from "@/hooks/useClaims";
import { SeedPapersSection } from "@/components/expand/SeedPapersSection";
import { useGuideState } from "@/hooks/useGuideState";
import type { ServerCounts } from "@/hooks/useGuideState";
import { toSnapshot } from "@/hooks/useConceptSnapshot";
import { hasResearchQuestion } from "@/lib/conceptGuidance";
import { computeRecommendations } from "@/lib/workflowAdvisor";
import { useWhereAmIStore } from "@/stores/useWhereAmIStore";
import type { CycleSignalsResponse } from "@/hooks/useCycleSignals";
import { CorpusSeedingPanel } from "@/components/expand/CorpusSeedingPanel";
import { useWorkingSets, useWorkingSetPaperIds, useWorkingSetActions } from "@/hooks/useWorkingSets";
import { useInboxActions } from "@/hooks/useInbox";
import { useCrossConceptSuggestions } from "@/hooks/useCrossConceptSuggestions";
import { useEvidenceStore } from "@/stores/evidence-store";
import { useDebateStore } from "@/stores/debate-store";
import { useFieldDebate } from "@/hooks/useFieldDebate";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useSeedDiscovery } from "@/hooks/useSeedDiscovery";
import { useSeedDiscoveryStore } from "@/stores/seed-discovery-store";
import { SeedDiscoveryDialog } from "@/components/expand/SeedDiscoveryDialog";
import { SearchResultsDialog } from "@/components/expand/SearchResultsDialog";
import type { ConceptSearchResponse } from "@/services/concept-search";
import { runCurateSearch } from "@/lib/curate-search";
import { guideSignals } from "@/lib/guideSignals";
import { staleScoredPaperIds } from "@/lib/conceptStaleness";
import { shouldAutoScore } from "@/lib/autoScore";
import { mergeScoreOverride } from "@/lib/paperMerge";
import { useSmartTrawlStore } from "@/stores/smart-trawl-store";
import { useZoteroBulkImportStore } from "@/stores/zotero-bulk-import-store";
import { useBibtexImportStore } from "@/stores/bibtex-import-store";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ReasonLine } from "@/components/ui/disabled-reason";
import { CampScopeBanner } from "@/components/analyze/CampScopeBanner";
import {
  encodeScopeForUrl,
  decodeScopeFromUrl,
} from "@/lib/paperIdScopeUrl";

const searchSchema = z.object({
  searchText: z.string().optional(),
  minCitations: z.coerce.number().optional(),
  earliestYear: z.coerce.number().optional(),
  isInfluential: z.coerce.boolean().optional(),
  minScore: z.coerce.number().optional(),
  contributionType: z.string().optional(),
  argumentationRole: z.string().optional(),

  overrideFilter: z.enum(["active", "championed", "dismissed", "none"]).optional(),
  readingStatus: z.enum(["unread", "skimmed", "read"]).optional(),
  hasEvidence: z.coerce.boolean().optional(),
  hasStashed: z.coerce.boolean().optional(),
  tagIds: z.array(z.string()).optional(),
  showDismissed: z.coerce.boolean().optional(),
  dismissedView: z.coerce.boolean().optional(),
  showFlagged: z.coerce.boolean().optional(),
  flaggedView: z.coerce.boolean().optional(),
  wsId: z.string().optional(),
  sortField: z.enum(["title", "year", "citations", "relevance_score", "reading_priority", "tension", "added_at"]).optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
  sourceFilter: z.enum(["monitored", "trawled", "imported"]).optional(),
  addedWithin: z.enum(["24h", "7d", "30d"]).optional(),
  // "Found via seed" filter. `.catch` drops a malformed id (hand-edited or
  // stale link) instead of throwing out of validateSearch and crashing Curate.
  viaSeed: z.string().uuid().optional().catch(undefined),
  // Facets 1c-2 angle tab: facet uuid or "bridges". `.catch` drops junk.
  angle: z.union([z.literal("bridges"), z.string().uuid()]).optional().catch(undefined),
  monitorInboxView: z.boolean().optional(),
  scope: z
    .string()
    .regex(/^(?:camp|bridges|tension):[\w-]+(?:__[\w-]+)?$/)
    .optional(),
  // Deep-link from the Zotero sync bell — selects a specific paper in
  // the detail panel after the paper list loads. Consumed once and
  // cleared from the URL so back-navigation doesn't keep re-selecting.
  selectedPaperId: z.string().uuid().optional(),
});

export const Route = createFileRoute("/concepts/$conceptId/curate")({
  component: ExpandPage,
  validateSearch: (search) => searchSchema.parse(search),
});

// All-false fallback for the advisor snapshot when cycle signals haven't loaded.
const EMPTY_CYCLE_SIGNALS: CycleSignalsResponse = {
  has_highlights: false,
  has_smart_trawl_papers: false,
  has_claims: false,
  has_claims_with_evidence: false,
  surface_gaps_enacted: false,
  refine_enacted: false,
  audit_enacted: false,
  drill_enacted: false,
  has_chat_messages: false,
  first_highlighted_paper_id: null,
};


function ExpandPage() {
  usePageView("/concepts/expand");
  const { conceptId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  // Data
  // isPending (no data yet), NOT isLoading (pending AND actively fetching):
  // a pending query whose retry is paused (offline blip, unfocused-window
  // retry pause) has isLoading=false — gating on isLoading rendered the
  // empty-corpus onboarding panel for a populated concept in that state.
  const { data: trawledData, isPending: trawledLoading } = usePapers(
    conceptId,
    { role: "trawled", mode: "all" },
  );
  const {
    data: foundationalData,
    isError: foundationalFailed,
    refetch: refetchFoundational,
  } = usePapers(conceptId, { role: "foundational", mode: "all" });

  // Mutations
  const promoteMutation = usePromotePapers(conceptId);
  const addPapers = useAddPapers(conceptId);
  const { trawl } = useTrawl(conceptId);
  const { startSnowball } = useSnowball(conceptId);

  const { startScoring, cancelScoring } = useScoring(conceptId);
  const { uploadAndImport } = useBibtexImport(conceptId);
  const { startRetrieval, cancelRetrieval, getDownloadUrl, extractAbstract } =
    usePdfRetrieval(conceptId);
  const { uploadPdf, isUploading } = usePdfUpload(conceptId);
  const scoreOverrideMutation = useScoreOverride(conceptId);
  const addExcerptMutation = useAddExcerpt(conceptId);
  const updateConceptMutation = useUpdateConcept(conceptId);
  const { data: concept } = useConcept(conceptId);
  const angles = useAngles(conceptId);
  const angleCtx = useMemo<AngleContext | undefined>(
    () => (angles.multi ? { facets: angles.facets, conceptScope: concept?.scope } : undefined),
    [angles.multi, angles.facets, concept?.scope],
  );
  // Drafted research question: "Looks right" clears the flag; "Edit" opens the
  // same ConceptPopover editor the rail renders, in a dialog (works with the
  // rail collapsed too). seedCount mirrors the rail ($conceptId.tsx).
  const confirmDrafted = useConfirmDraftedQuestion(conceptId);
  const [conceptEditorOpen, setConceptEditorOpen] = useState(false);
  const { data: paperCounts } = usePapersCount(conceptId);
  // Cross-route navigation for Next step actions that hand off to Analyze.
  // Route.useNavigate() is bound to this route's search schema; the global
  // useNavigate() type-checks against the target (analyze) route instead.
  const crossNavigate = useNavigate();
  const summarizeMutation = useSummarizePaper(conceptId);
  const explainMutation = useExplainSelection(conceptId);
  const readingStatusMutation = useReadingStatus(conceptId);
  const notesMutation = useNotes(conceptId);
  const { data: tagsData } = useTags(conceptId);
  const { createTag: createTagMutation, updateTag: updateTagMutation } = useTagActions(conceptId);
  const updatePaperTagsMutation = useUpdatePaperTags(conceptId);
  const updatePaperMutation = useUpdatePaper(conceptId);
  const { data: currentUser } = useCurrentUser();

  // Filter presets
  const { data: presetsData } = useFilterPresets(conceptId);
  const { createPreset, deletePreset, updatePreset } = useFilterPresetActions(conceptId);

  // Working Sets + Inbox
  const wsId = search.wsId;
  const { data: workingSets } = useWorkingSets(conceptId);
  const { data: wsPaperIdsData } = useWorkingSetPaperIds(conceptId, wsId);
  const wsActions = useWorkingSetActions(conceptId);
  const inboxActions = useInboxActions();

  // Evidence map
  const { data: claimsData } = useClaims(conceptId);

  // Field debate (for tension sort)
  const { data: debateMap } = useFieldDebate(conceptId);

  // Debate camp filter (set when navigating from Field Debate View)
  const campPaperIds = useDebateStore((s) => s.campPaperIds);
  const campLabel = useDebateStore((s) => s.campLabel);
  const clearCampFilter = useDebateStore((s) => s.clearCampFilter);

  // Monitor
  const monitorSettingsMutation = useMonitorSettings(conceptId);
  const { startMonitor, cancelMonitor } = useMonitorRun(conceptId);
  const isMonitorRunning = useMonitorStore((s) => s.isRunning);
  const triageMutation = useTriagePaper(conceptId);
  const triageBatchMutation = useTriageBatch(conceptId);

  // Seed discovery
  const { discover } = useSeedDiscovery(conceptId);
  // An angle-owned run (Add-angle sheet) must not pop the concept dialog (1c-3).
  const seedDiscoveryCandidates = useSeedDiscoveryStore((s) => (s.owner === "concept" ? s.candidates : null));

  // Dismiss / restore mutations
  const queryClient = useQueryClient();

  // Any change to a paper's exclusion_reason changes the analyze scope.
  // The Landscape rail pill detects this via a fingerprint comparison
  // (cached fingerprint vs current displayPaperIds), so we only need to
  // invalidate the papers query here — no explicit landscape cache
  // clear. Keeping the cached LandscapeData lets the rail render STALE
  // until the user opens the overlay (which then refetches on stale).
  const invalidatePapers = () => {
    queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
  };

  const dismissMutation = useMutation({
    mutationFn: async ({ paperId, reason, reasonText }: { paperId: string; reason: string; reasonText?: string }) => {
      return apiClient.patch(`api/concepts/${conceptId}/papers/${paperId}/dismiss`, {
        json: { reason, reason_text: reasonText },
        timeout: 30_000,
      }).json();
    },
    onMutate: async ({ paperId, reason, reasonText }) => {
      await queryClient.cancelQueries({ queryKey: ["papers", conceptId] });
      // Match the backend's format: `{reason}: {reason_text}` when text is present.
      const fullReason = reasonText ? `${reason}: ${reasonText}` : reason;
      queryClient.setQueriesData<{ papers: PaperWithRole[] }>(
        { queryKey: ["papers", conceptId] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            papers: old.papers.map((p) =>
              p.id === paperId ? { ...p, exclusion_reason: fullReason } : p,
            ),
          };
        },
      );
    },
    onError: invalidatePapers,
    onSettled: invalidatePapers,
  });

  const restoreMutation = useMutation({
    mutationFn: async (paperId: string) => {
      return apiClient.patch(`api/concepts/${conceptId}/papers/${paperId}/restore`, {
        timeout: 30_000,
      }).json();
    },
    onMutate: async (paperId) => {
      await queryClient.cancelQueries({ queryKey: ["papers", conceptId] });
      queryClient.setQueriesData<{ papers: PaperWithRole[] }>(
        { queryKey: ["papers", conceptId] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            papers: old.papers.map((p) =>
              p.id === paperId ? { ...p, exclusion_reason: null } : p,
            ),
          };
        },
      );
    },
    onError: invalidatePapers,
    onSettled: invalidatePapers,
  });

  const bulkDismissMutation = useMutation({
    mutationFn: async ({ paperIds, reason, reasonText }: { paperIds: string[]; reason: string; reasonText?: string }) => {
      return apiClient.post(`api/concepts/${conceptId}/papers/bulk-dismiss`, {
        json: { paper_ids: paperIds, reason, reason_text: reasonText },
        timeout: 30_000,
      }).json();
    },
    onMutate: async ({ paperIds, reason, reasonText }) => {
      await queryClient.cancelQueries({ queryKey: ["papers", conceptId] });
      // Match the backend's format: `{reason}: {reason_text}` when text is present.
      const fullReason = reasonText ? `${reason}: ${reasonText}` : reason;
      queryClient.setQueriesData<{ papers: PaperWithRole[] }>(
        { queryKey: ["papers", conceptId] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            papers: old.papers.map((p) =>
              paperIds.includes(p.id) ? { ...p, exclusion_reason: fullReason } : p,
            ),
          };
        },
      );
    },
    onError: invalidatePapers,
    onSettled: () => {
      invalidatePapers();
      useSelectionStore.getState().deselectAll();
    },
  });

  // Abstract backfill
  const [backfillProgress, setBackfillProgress] = useState<{ total: number; remaining: number } | null>(null);
  const backfillAbstracts = useMutation({
    mutationFn: () =>
      apiClient
        .post(`api/concepts/${conceptId}/pdf/backfill-abstracts`)
        .json<{ queued: number }>(),
    onSuccess: (data: { queued: number }) => {
      if (data.queued > 0) {
        setBackfillProgress({ total: data.queued, remaining: data.queued });
        let lastRemaining = data.queued;
        let stableCount = 0;
        const poll = setInterval(async () => {
          try {
            const status = await apiClient
              .get(`api/concepts/${conceptId}/pdf/abstract-backfill-status`)
              .json<{ remaining: number }>();
            setBackfillProgress((prev) => prev ? { ...prev, remaining: status.remaining } : null);
            // Only refetch papers when count actually changed
            if (status.remaining !== lastRemaining) {
              lastRemaining = status.remaining;
              stableCount = 0;
              queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
            } else {
              stableCount++;
            }
            // Stop when count is 0 or stable for 3 consecutive polls (backfill done)
            if (status.remaining === 0 || stableCount >= 3) {
              clearInterval(poll);
              setBackfillProgress(null);
              queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
              toast.success("Abstract backfill complete");
            }
          } catch {
            clearInterval(poll);
            setBackfillProgress(null);
          }
        }, 3000);
      } else {
        toast.info("No papers need abstract fetching");
      }
    },
  });

  // PDF retrieval state (used by PaperDetail)
  const isRetrieving = usePdfStore((s) => s.isRetrieving);

  // OperationBar active state (for bottomOffset)
  const pdfActive = usePdfStore((s) => s.isRetrieving || s.isCompleted || !!s.error);
  const scoringActive = useScoringStore((s) => s.isScoring || s.isCompleted || !!s.error);
  const trawlActive = useTrawlStore((s) => s.isTrawling || s.isCompleted || !!s.error);
  const snowballActive = useSnowballStore((s) => s.isSnowballing || s.isCompleted || !!s.error);
  const monitorActive = useMonitorStore((s) => s.isRunning || s.isCompleted || !!s.error);
  const hasActiveOps = pdfActive || scoringActive || trawlActive || snowballActive || monitorActive;

  // ActionBar busy gate — IN-FLIGHT states only. The *Active flags above
  // include terminal completed/error states because they drive pill-bar
  // VISIBILITY (bottomOffset); reusing them as `busy` held the whole bar
  // locked behind a stale error pill (e.g. a cancelled auto-score) until
  // the pill was click-dismissed.
  const scoringInFlight = useScoringStore((s) => s.isScoring);
  const trawlInFlight = useTrawlStore((s) => s.isTrawling);
  const snowballInFlight = useSnowballStore((s) => s.isSnowballing);

  // Selection
  const { selectedIds, selectAll, deselectAll } = useSelectionStore();

  // Detail panel
  const [selectedPaper, setSelectedPaper] = useState<PaperWithRole | null>(
    null,
  );
  const [, setTrawlClicked] = useState(false);
  const [, setScoreClicked] = useState(false);
  // Session-only "I'll add papers manually later" — keyed by concept so a
  // skip on one concept doesn't hide the seeding panel on the next.
  const [seedingSkippedFor, setSeedingSkippedFor] = useState<string | null>(null);

  // Highlights for selected paper (Spot Trawl)
  const {
    highlights, selectedIds: selectedHighlightIds,
    createHighlight, deleteHighlight, toggleSelection: toggleHighlight,
    selectAll: selectAllHighlights, clearAll: clearAllHighlights,
  } = useHighlights(selectedPaper?.id, conceptId);
  const annotationImport = useAnnotationImport(selectedPaper?.id, conceptId);
  // Persistent evidence clips for the detail panel — what's already been
  // pulled from this paper (survives reloads; the pending tray does not).
  const evidenceClips = useMemo(
    () => highlights.filter((h) => h.source === "evidence"),
    [highlights],
  );
  const smartTrawl = useSmartTrawl(conceptId);
  const { data: cycleSignals } = useCycleSignals(conceptId);

  // SPECTER2 cross-concept suggestions for selected paper
  const { data: crossConceptData } = useCrossConceptSuggestions(
    conceptId,
    selectedPaper?.id,
  );

  // PDF / full text viewer state
  const [pdfViewMode, setPdfViewMode] = useState<"info" | "pdf" | "fulltext">("info");
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  // Which paper the loaded pdfUrl belongs to. Travels with every setPdfUrl so
  // reading-state automation can't credit a freshly-selected paper with the
  // previous paper's still-displayed PDF (one-render gap before the clear
  // effect runs — Codex P2).
  const [pdfPaperId, setPdfPaperId] = useState<string | null>(null);
  const [pdfSource, setPdfSource] = useState<"user" | "canonical" | "none">("none");
  // Source of the currently-displayed AI summary (per-paper) — lets us surface
  // the "Regenerate from your upload" button when a canonical summary exists
  // but the user has now uploaded their own PDF.
  const [summarySource, setSummarySource] = useState<"user" | "canonical" | undefined>(undefined);
  const [fullText, setFullText] = useState<string | null>(null);
  const [pdfMaximized, setPdfMaximized] = useState(false);
  const [pdfCurrentPage, setPdfCurrentPage] = useState(1);

  // Page count of the loaded Curate PDF (null until onNumPages reports).
  const [pdfNumPages, setPdfNumPages] = useState<number | null>(null);

  // Reading-state automation (spec 2026-06-07, amended 2026-09-27): only
  // content moves status — PDF/full-text engagement skims (15s active or 2
  // pages), PDF pages + active time reads. Abstract/detail dwell never does.
  // Server enforces all guardrails. pdfPaperId (not selectedPaper.id)
  // attributes PDF time to the paper whose PDF is actually loaded —
  // selection can change a render ahead of the viewer clearing. Full text
  // is fetched for (and guarded to) the selected paper.
  const fullTextOpen = pdfViewMode === "fulltext" && !!fullText;
  useReadingAutopromote(conceptId, selectedPaper, {
    paperId: pdfViewMode === "pdf" && pdfUrl
      ? pdfPaperId
      : fullTextOpen
        ? (selectedPaper?.id ?? null)
        : null,
    page: pdfCurrentPage,
    pdfOpen: pdfViewMode === "pdf" && !!pdfUrl && !!pdfPaperId,
    numPages: pdfNumPages,
    fullTextOpen,
  });

  const [jumpToPage, setJumpToPage] = useState<{ page: number; key: number } | null>(null);
  const [focusedHighlightId, setFocusedHighlightId] = useState<string | null>(null);

  // Auto-clear highlight focus after 3 seconds
  useEffect(() => {
    if (!focusedHighlightId) return;
    const timer = setTimeout(() => setFocusedHighlightId(null), 3000);
    return () => clearTimeout(timer);
  }, [focusedHighlightId]);

  // Clear dialog
  const [showStartOver, setShowStartOver] = useState(false);
  const undoReset = useUndoReset(conceptId);

  // Bulk dismiss dialog
  const [bulkDismissOpen, setBulkDismissOpen] = useState(false);

  // Filter-complement cull dialog ("Dismiss hidden")
  const [cullDialogOpen, setCullDialogOpen] = useState(false);
  const [findSimilarModal, setFindSimilarModal] = useState<{
    paperId: string;
    reason: string;
    reasonText?: string;
  } | null>(null);

  // Bulk import dialog
  const [showBulkImport, setShowBulkImport] = useState(false);

  // Single-file PDF upload attestation dialog — per-user uploads must carry
  // rights attestation (backend /confirm-upload gates on it). Holds the pending
  // (paperId, file) until the user checks the box and confirms.
  const [pendingUpload, setPendingUpload] = useState<{
    paperId: string;
    file: File;
  } | null>(null);
  const [uploadAttested, setUploadAttested] = useState(false);

  // Search loading state
  const [isSearching, setIsSearching] = useState(false);
  // Two-source search preview (OpenAlex slice 1b, ring-gated by the backend).
  const [searchPreview, setSearchPreview] = useState<
    { seq: number; conceptId: string; query: string; response: ConceptSearchResponse } | null
  >(null);
  const searchPreviewEnabled = concept?.search_preview_enabled ?? false;

  // Explanation state
  const [explanation, setExplanation] = useState<{
    text: string;
    result: string;
  } | null>(null);

  // Search input ref for Ctrl+K shortcut
  const bibFileRef = useRef<HTMLInputElement>(null);

  // Filter persistence: restore from store or default preset on mount
  const storedState = useFilterStore((s) => s.filters[conceptId]);
  const setStoredFilters = useFilterStore((s) => s.setFilters);
  const paperIdScope = storedState?.paperIdScope;
  const paperIdScopeLabel = storedState?.paperIdScopeLabel;
  const paperIdScopeKind = storedState?.paperIdScopeKind;
  const clearPaperIdScope = useFilterStore((s) => s.clearPaperIdScope);
  const setPaperIdScope = useFilterStore((s) => s.setPaperIdScope);

  // Effect A: store → URL (write scope param when camp scope is active).
  // Guard on `debateMap`: on hard refresh of /expand?scope=…, the store
  // has no `paperIdScopeKind` yet (no persist middleware) and the
  // debateMap query is still in flight. Without this guard, Effect A
  // fires first, sees `!paperIdScopeKind` + URL scope, and strips the
  // URL before Effect B can decode it. After the strip, Effect B's
  // `!search.scope` guard traps it permanently and the page drops to
  // Full Corpus despite the URL having claimed a scope.
  useEffect(() => {
    if (!debateMap) return;
    if (!paperIdScopeKind) {
      if (search.scope) {
        navigate({ search: (prev) => ({ ...prev, scope: undefined }), replace: true });
      }
      return;
    }
    const encoded = encodeScopeForUrl(paperIdScopeKind, paperIdScope, paperIdScopeLabel, debateMap);
    if (encoded && encoded !== search.scope) {
      navigate({ search: (prev) => ({ ...prev, scope: encoded }), replace: true });
    }
  }, [paperIdScopeKind, paperIdScope, paperIdScopeLabel, debateMap, search.scope, navigate]);

  // Effect B: URL → store (decode on mount + debateMap load)
  useEffect(() => {
    if (!search.scope || !debateMap) return;
    const decoded = decodeScopeFromUrl(search.scope, debateMap);
    if (!decoded) return;
    // Only apply if store doesn't already match (avoids effect cascade)
    const current = useFilterStore.getState().filters[conceptId];
    if (current?.paperIdScopeLabel === decoded.label) return;
    setPaperIdScope(conceptId, decoded.paperIds, decoded.label, decoded.kind);
  }, [search.scope, debateMap, conceptId, setPaperIdScope]);

  const restored = useRef(false);

  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    const hasUrlFilters =
      search.searchText ||
      search.minCitations != null ||
      search.earliestYear != null ||
      search.isInfluential ||
      search.minScore != null ||
      search.contributionType ||
      search.argumentationRole ||
      search.overrideFilter ||
      search.readingStatus ||
      search.hasEvidence ||
      (search.tagIds && search.tagIds.length > 0) ||
      search.viaSeed ||
      search.angle ||
      search.sortField;
    if (!hasUrlFilters && storedState) {
      const { filters: sf, sortField: storedSort, sortDirection: storedDir } = storedState;
      const hasStored = Object.values(sf).some((v) => v !== undefined) || storedSort;
      if (hasStored) {
        navigate({ search: { ...sf, sortField: storedSort, sortDirection: storedDir }, replace: true });
        return;
      }
    }
    // Load default preset if no other filters
    if (!hasUrlFilters && !storedState && presetsData) {
      const defaultPreset = presetsData.find((p) => p.is_default);
      if (defaultPreset) {
        const pf = defaultPreset.filters;
        navigate({
          search: {
            ...pf,
            sortField: defaultPreset.sort_field as SortField | undefined,
            sortDirection: defaultPreset.sort_direction as SortDirection | undefined,
          },
          replace: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetsData]);

  // Filters from URL search params — memoized to prevent useFilteredPapers
  // from recomputing on every render (which would give PaperTable a new
  // array reference each time, defeating its React.memo wrapper).
  const sortField = (search.sortField as SortField | undefined) ?? "relevance_score";
  const sortDirection = (search.sortDirection as SortDirection) ?? "desc";

  const filters: PaperFilters = useMemo(() => ({
    searchText: search.searchText,
    minCitations: search.minCitations,
    earliestYear: search.earliestYear,
    isInfluential: search.isInfluential,
    minScore: search.minScore,
    contributionType: search.contributionType,
    argumentationRole: search.argumentationRole,
    overrideFilter: (search.dismissedView || search.flaggedView) ? undefined : (search.overrideFilter ?? "active"),
    readingStatus: search.readingStatus,
    hasEvidence: search.hasEvidence,
    hasStashed: search.hasStashed,
    tagIds: search.tagIds,
    showDismissed: search.showDismissed,
    dismissedView: search.dismissedView,
    showFlagged: search.flaggedView ? true : search.showFlagged,
    flaggedView: search.flaggedView,
    sourceFilter: search.sourceFilter,
    addedWithin: search.addedWithin,
    viaSeed: search.viaSeed,
    angle: search.angle,
    monitorInboxView: search.monitorInboxView,
  }), [
    search.searchText, search.minCitations, search.earliestYear,
    search.isInfluential, search.minScore, search.contributionType,
    search.argumentationRole, search.overrideFilter,
    search.readingStatus, search.hasEvidence, search.hasStashed, search.tagIds, search.showDismissed,
    search.dismissedView, search.showFlagged, search.flaggedView,
    search.sourceFilter, search.addedWithin, search.viaSeed, search.angle, search.monitorInboxView,
  ]);
  // The angle tab is Curate-URL state only: Analyze reads the store and must
  // stay whole-concept (spec §5 Analyses; its analytics shape has no angle_scores).
  // Same angle-less set drives the cull: the angle tab is a view, not a filter.
  const storeFilters = useMemo(() => cullFilters(filters), [filters]);

  // Per-concept mount: if the URL has no wsId but the store does (user
  // came from Analyze where saving a camp scope writes wsId to the store
  // only), mirror store→URL so the URL-driven state below picks it up.
  // Without this, the URL→store effect that follows would unconditionally
  // overwrite store.wsId with undefined and the just-saved Working Set
  // would silently drop back to Full Corpus.
  useEffect(() => {
    const slice = useFilterStore.getState().filters[conceptId];
    if (!search.wsId && slice?.wsId) {
      navigate({ search: { ...search, wsId: slice.wsId }, replace: true });
    }
    // Intentionally only depend on conceptId — runs once per concept change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conceptId]);

  // Mirror URL-derived filters into the store so other routes (Analyze)
  // can read the same filter state. Without this, only filter changes
  // made through handleFiltersChange (FilterBar UI) propagate to the
  // store — URL-set filters from refresh / bookmark / direct nav stay
  // invisible to consumers that read the store.
  //
  // Skip the mirror when paperIdScope is active: the slice's scope state
  // takes precedence over URL filters, and `setFilters`'s spread
  // preserves paperIdScope* — so without the gate the mirror would
  // silently land filters/wsId from URL alongside an active scope,
  // violating the documented mutual-exclusion invariant. Effect A/B
  // own the slice while scope is active; mirror defers to them.
  useEffect(() => {
    if (paperIdScope) return;
    setStoredFilters(conceptId, storeFilters, sortField, sortDirection, search.wsId);
  }, [paperIdScope, storeFilters, sortField, sortDirection, search.wsId, conceptId, setStoredFilters]);

  const allPapers = useMemo(() => trawledData?.papers ?? [], [trawledData]);

  // Deep-link consumer is below the foundationalPapers declaration —
  // search both trawled and foundational lists.
  const selectedPaperIdFromUrl = search.selectedPaperId;

  // When a working set is active, scope papers to only those in the WS
  const papers = useMemo(() => {
    let result = allPapers;
    if (wsId && wsPaperIdsData) {
      const idSet = new Set(wsPaperIdsData.paper_ids);
      result = result.filter((p) => idSet.has(p.id));
    }
    if (campPaperIds) {
      const campSet = new Set(campPaperIds);
      result = result.filter((p) => campSet.has(p.id));
    }
    return result;
  }, [allPapers, wsId, wsPaperIdsData, campPaperIds]);
  // Facet-bar counts: the same scoped base as the table (incl. paperIdScope),
  // dismissed excluded.
  const angleBase = useMemo(
    () => applyPaperIdScope(papers, paperIdScope).filter((p) => !p.exclusion_reason),
    [papers, paperIdScope],
  );
  // Refetch papers when the background angle pass finishes.
  useAngleRescoreRefresh(conceptId, concept?.angle_rescore_pending);
  const { filtered, total } = useFilteredPapers(papers, filters, sortField, sortDirection, debateMap, paperIdScope, angleCtx);
  // Cull's "displayed" set ignores the angle tab (unsorted — only ids matter),
  // so "Dismiss hidden" never reaches papers that merely belong to other angles.
  const { filtered: cullDisplayed } = useFilteredPapers(papers, storeFilters, undefined, undefined, debateMap, paperIdScope);

  // Filter-complement cull set: what the active filters are hiding, minus
  // protected classes. Computed against the same scoped base the visible
  // table draws from, so inside a working set "others" never reaches outside
  // it. Meaningless in the special views, where the complement of what's
  // shown isn't "papers I filtered out".
  const cullSet = useMemo(() => {
    if (search.dismissedView || search.flaggedView || search.monitorInboxView) {
      return { cullIds: [], protectedChampioned: 0, protectedUntriaged: 0 };
    }
    const displayedIds = new Set(cullDisplayed.map((p) => p.id));
    return computeCullSet(applyPaperIdScope(papers, paperIdScope), displayedIds);
  }, [search.dismissedView, search.flaggedView, search.monitorInboxView, cullDisplayed, papers, paperIdScope]);

  // Auto-select paper when arriving from Landscape
  useEffect(() => {
    const pendingId = sessionStorage.getItem("paperchase-landscape-paper");
    if (!pendingId || filtered.length === 0) return;
    sessionStorage.removeItem("paperchase-landscape-paper");
    const paper = filtered.find((p) => p.id === pendingId);
    if (paper) setSelectedPaper(paper);
  }, [filtered]);

  // Arrow key navigation: move through filtered papers even when PaperDetail has focus
  const filteredRef = useRef(filtered);
  filteredRef.current = filtered;
  const arrowNavSelectedRef = useRef(selectedPaper);
  arrowNavSelectedRef.current = selectedPaper;
  const pdfMaximizedRef = useRef(pdfMaximized);
  pdfMaximizedRef.current = pdfMaximized;
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!arrowNavSelectedRef.current) return;
      if (!shouldArrowNavigatePapers(e, { pdfMaximized: pdfMaximizedRef.current })) return;

      e.preventDefault();
      const list = filteredRef.current;
      const idx = list.findIndex((p) => p.id === arrowNavSelectedRef.current?.id);
      if (idx < 0) return;
      const nextIdx = e.key === "ArrowDown"
        ? Math.min(idx + 1, list.length - 1)
        : Math.max(idx - 1, 0);
      if (nextIdx !== idx) setSelectedPaper(list[nextIdx]);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const foundationalPapers = useMemo(() => foundationalData?.papers ?? [], [foundationalData]);

  // Provenance: current seeds (Seeds column k/n + "Found via seed" filter)
  // and an id → paper index for the detail panel's "How PaperChase found this".
  const seedIdSet = useMemo(() => new Set(foundationalPapers.map((p) => p.id)), [foundationalPapers]);
  const seedOptions = useMemo(
    () => foundationalPapers.map((p) => ({ id: p.id, title: p.title })),
    [foundationalPapers],
  );
  const paperIndex = useMemo(
    () => new Map([...foundationalPapers, ...allPapers].map((p) => [p.id, p] as const)),
    [foundationalPapers, allPapers],
  );

  // Consume ?selectedPaperId=… deep-link from the Zotero sync bell.
  // Search both lists — a Zotero attach can land on a foundational paper
  // too. Strip the param after consumption so back-nav doesn't re-select.
  useEffect(() => {
    if (!selectedPaperIdFromUrl) return;
    if (allPapers.length === 0 && foundationalPapers.length === 0) return;
    const found =
      allPapers.find((p) => p.id === selectedPaperIdFromUrl) ??
      foundationalPapers.find((p) => p.id === selectedPaperIdFromUrl);
    if (found) setSelectedPaper(found);
    navigate({
      search: (prev) => ({ ...prev, selectedPaperId: undefined }),
      replace: true,
    });
  }, [selectedPaperIdFromUrl, allPapers, foundationalPapers, navigate]);
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  const monitorRecent = useMemo(() => {
    const cutoff = Date.now() - SEVEN_DAYS_MS;
    return allPapers.filter((p) => p.added_source === "monitor" && p.added_at && new Date(p.added_at).getTime() > cutoff);
  }, [allPapers]);
  const monitorLastRun = useMemo(() => {
    if (!concept?.last_monitor_run) return [];
    const cutoff = new Date(concept.last_monitor_run).getTime();
    return allPapers.filter((p) => p.added_source === "monitor" && p.added_at && new Date(p.added_at).getTime() >= cutoff);
  }, [allPapers, concept?.last_monitor_run]);

  // Guide system — running state reads
  const seedDiscoveryRunning = useSeedDiscoveryStore((s) => s.owner === "concept" && s.isRunning);

  const serverCounts: ServerCounts = useMemo(() => ({
    seedCount: foundationalPapers.length,
    trawledCount: allPapers.length,
    scoredCount: allPapers.filter((p) => p.scoring_timestamp != null).length,
    readOrSkimmedCount: allPapers.filter(
      (p) => p.reading_status === "read" || p.reading_status === "skimmed",
    ).length,
    claimCount: claimsData?.length ?? 0,
    totalPapers: foundationalPapers.length + allPapers.length,
    hasDebateMap: !!debateMap,
    hasClaimsWithEvidence: claimsData?.some((c) => c.evidence_count > 0) ?? false,
    monitorEnabled: concept?.monitor_enabled ?? false,
    conceptCreatedAt: concept?.created_at ?? new Date().toISOString(),
    digestFrequency: currentUser?.digest_frequency ?? "",
    hasWorkingSets: (workingSets?.length ?? 0) > 0,
  }), [foundationalPapers, allPapers, claimsData, debateMap, concept, currentUser, workingSets]);

  const guide = useGuideState(conceptId, serverCounts);

  const handleSaveCampScopeAsWorkingSet = useCallback(() => {
    if (!paperIdScope || !paperIdScopeLabel) return;
    const name = `${paperIdScopeLabel} (Field Debate)`;
    const paperIds = paperIdScope;
    const label = paperIdScopeLabel;
    const kind = paperIdScopeKind;
    const tempId = wsActions.saveAsWorkingSet({
      name,
      paperIds,
      source: kind ?? "field-debate-camp",
      onSuccess: (realWs, tId) => {
        const cur = useFilterStore.getState().filters[conceptId];
        if (cur?.wsId === tId) {
          setStoredFilters(conceptId, {} as PaperFilters, undefined, undefined, realWs.id);
          // Swap URL tempId → realWs.id so the URL stays in sync; without
          // this the URL still says ?wsId=temp-… and the next URL→store
          // mirror would re-introduce the stale tempId.
          navigate({ search: { ...search, scope: undefined, wsId: realWs.id }, replace: true });
        }
      },
      onError: (e) => {
        if (kind) {
          useFilterStore.getState().setPaperIdScope(conceptId, paperIds, label, kind);
        }
        toast.error(`Couldn't save working set: ${e.message}`);
      },
    });
    clearPaperIdScope(conceptId);
    setStoredFilters(conceptId, {} as PaperFilters, undefined, undefined, tempId);
    // Update URL to reflect the new wsId. On Expand, wsId is URL-derived
    // (`const wsId = search.wsId`), so without this the page immediately
    // drops back to Full Corpus despite the success toast — the store
    // has the new wsId but the URL doesn't, and the next FilterBar
    // interaction would clobber the store's wsId with search.wsId
    // (undefined) too.
    navigate({ search: { ...search, scope: undefined, wsId: tempId } });
    toast.success(`Saved ${paperIds.length} papers as "${name}"`);
  }, [
    paperIdScope,
    paperIdScopeLabel,
    paperIdScopeKind,
    conceptId,
    wsActions,
    clearPaperIdScope,
    setStoredFilters,
    navigate,
    search,
  ]);

  // Prompt to add new papers to working set after trawl/snowball completes
  const trawlCompleted = useTrawlStore((s) => s.isCompleted);
  const snowballCompleted = useSnowballStore((s) => s.isCompleted);
  const smartTrawlCompleted = useSmartTrawlStore((s) => s.isCompleted);
  // Deliberate imports that add new unscored papers — auto-score them too.
  // Both hooks await the ["papers"] refetch before flipping these signals,
  // so the auto-score effect below reads a fresh allPapers on the edge.
  const zoteroImportCompleted = useZoteroBulkImportStore((s) => s.status === "done");
  const bibtexImportCompleted = useBibtexImportStore((s) => s.isCompleted);
  const prevTrawlRef = useRef(false);
  const prevSnowballRef = useRef(false);
  useEffect(() => {
    if (!wsId) { prevTrawlRef.current = trawlCompleted; prevSnowballRef.current = snowballCompleted; return; }
    const activeWs = workingSets?.find((ws) => ws.id === wsId);
    if (!activeWs) return;
    const showPrompt = (completed: boolean, prevRef: React.MutableRefObject<boolean>) => {
      if (completed && !prevRef.current) {
        const wsIds = new Set(wsPaperIdsData?.paper_ids ?? []);
        const newPapers = allPapers.filter((p) => !wsIds.has(p.id));
        if (newPapers.length > 0) {
          toast(`${newPapers.length} new papers added to corpus`, {
            description: `Add to "${activeWs.name}"?`,
            action: {
              label: "Add",
              onClick: () => wsActions.addPapers.mutate({ wsId, paperIds: newPapers.map((p) => p.id) }),
            },
          });
        }
      }
      prevRef.current = completed;
    };
    showPrompt(trawlCompleted, prevTrawlRef);
    showPrompt(snowballCompleted, prevSnowballRef);
  }, [trawlCompleted, snowballCompleted, wsId, workingSets, allPapers, wsPaperIdsData, wsActions.addPapers]);

  // Keep selectedPaper in sync with query cache so detail pane reflects
  // updated pdf_status etc. after refetches. Uses paper ID tracking to
  // avoid fighting with optimistic updates from spread operators.
  const lastSyncedPapersRef = useRef(allPapers);
  const lastSyncedFoundationalRef = useRef(foundationalPapers);
  useEffect(() => {
    if (!selectedPaper) return;
    const papersChanged = allPapers !== lastSyncedPapersRef.current;
    const foundationalChanged = foundationalPapers !== lastSyncedFoundationalRef.current;
    if (!papersChanged && !foundationalChanged) return;
    lastSyncedPapersRef.current = allPapers;
    lastSyncedFoundationalRef.current = foundationalPapers;
    const fresh =
      allPapers.find((p) => p.id === selectedPaper.id) ??
      foundationalPapers.find((p) => p.id === selectedPaper.id);
    if (fresh) {
      // Check if paper is still in the filtered (visible) list
      const stillVisible = filtered.some((p) => p.id === fresh.id);
      if (stillVisible) {
        setSelectedPaper(fresh);
      } else {
        // Paper was dismissed/filtered out — move to next visible paper
        const nextPaper = filtered.length > 0 ? filtered[0] : null;
        setSelectedPaper(nextPaper);
        if (!nextPaper) handleClosePdfViewer();
      }
    } else {
      // Paper was removed (cleared) — move to next visible paper
      const nextPaper = filtered.length > 0 ? filtered[0] : null;
      setSelectedPaper(nextPaper);
      if (!nextPaper) handleClosePdfViewer();
    }
    // Only run when papers array references change (query refetch), not on selectedPaper changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPapers, foundationalPapers]);

  // Compute scoring counts and IDs from filtered (visible) papers
  const {
    unscoredCount,
    unscoredIds,
    needsRescoreCount,
    needsRescoreIds,
  } = useMemo(() => {
    const unscoredList: string[] = [];
    const needsRescoreList: string[] = [];
    for (const p of filtered) {
      if (p.relevance_score === 0) unscoredList.push(p.id);
      if (
        p.relevance_score > 0 &&
        p.full_text_status === "fetched" &&
        (!p.scoring_timestamp ||
          (p.canonical_pdf_retrieval_at &&
            p.canonical_pdf_retrieval_at > p.scoring_timestamp))
      ) {
        needsRescoreList.push(p.id);
      }
    }
    return {
      unscoredCount: unscoredList.length,
      unscoredIds: unscoredList,
      needsRescoreCount: needsRescoreList.length,
      needsRescoreIds: needsRescoreList,
    };
  }, [filtered]);

  // Scoring quota calculations (use allPapers — quota is concept-level, not WS-scoped)
  const scoringLimit = currentUser?.plan_limits?.scoring_per_concept ?? null;
  const scoredCount = useMemo(
    () => allPapers.filter((p: any) => (p.relevance_score ?? 0) > 0).length,
    [allPapers],
  );
  const atScoringLimit = scoringLimit != null && scoredCount >= scoringLimit;

  // Concept-level unscored (NOT filtered) — for the nudge, which must see
  // papers even when a filter hides them. Dismissed papers are excluded:
  // they're not scoreable inventory, and one dismissed score-0 row kept the
  // "Score N unscored papers" rec permanently alive with nothing to act on.
  const conceptUnscoredIds = useMemo(() => {
    const ids: string[] = [];
    for (const p of allPapers) {
      // A scored 0 (e.g. no abstract) is a result, not a to-do: rescoring it
      // returns 0 again, so the nudge would never clear. Same rule as the
      // server's papers/count `unscored`.
      if (p.relevance_score === 0 && !p.scoring_timestamp && !p.exclusion_reason) ids.push(p.id);
    }
    return ids;
  }, [allPapers]);
  const conceptUnscoredCount = conceptUnscoredIds.length;
  const isScoringNow = useScoringStore((s) => s.isScoring);
  const backgroundScoring = useBackgroundScoringPending(conceptId);

  // Auto-score after trawl/snowball/spot-trawl imports new papers
  const prevAutoScoreTrawlRef = useRef(false);
  const prevAutoScoreSnowballRef = useRef(false);
  const prevAutoScoreSmartTrawlRef = useRef(false);
  const prevAutoScoreZoteroRef = useRef(false);
  const prevAutoScoreBibtexRef = useRef(false);
  useEffect(() => {
    const trigger = (
      completed: boolean,
      prevRef: React.MutableRefObject<boolean>,
      showToast: boolean,
    ) => {
      if (completed && !prevRef.current) {
        // A pending background batch counts as running: the server 409s a
        // second run, which surfaced as a "Scoring error" toast after imports.
        const scoringRunning = useScoringStore.getState().isScoring || backgroundScoring;
        if (
          shouldAutoScore({
            unscoredCount: conceptUnscoredIds.length,
            hasDescription: hasResearchQuestion(concept?.abstract),
            scoringRunning,
            atScoringLimit,
          })
        ) {
          if (showToast) toast.info("Papers imported — scoring automatically...");
          startScoring({ paperIds: conceptUnscoredIds });
        } else if (backgroundScoring && conceptUnscoredIds.length > 0) {
          toast.info(`${conceptUnscoredIds.length} new paper${conceptUnscoredIds.length === 1 ? "" : "s"} not scored yet`, {
            description: "Background scoring is still running — score the new papers once it finishes.",
          });
        } else if (conceptUnscoredIds.length > 0 && !hasResearchQuestion(concept?.abstract)) {
          toast.info("Add a research question to score these papers", {
            description: "PaperChase grades every paper against your research question.",
          });
        }
      }
      prevRef.current = completed;
    };
    trigger(trawlCompleted, prevAutoScoreTrawlRef, true);
    trigger(snowballCompleted, prevAutoScoreSnowballRef, true);
    // Spot Trawl: hook already toasts the add ("Spot Trawl added N papers")
    // and the OperationBar shows scoring progress, so no second toast here.
    trigger(smartTrawlCompleted, prevAutoScoreSmartTrawlRef, false);
    // Deliberate imports (Zotero bulk import, BibTeX upload) that add new
    // unscored papers. Both hooks await the ["papers"] refetch before the
    // completion signal flips, so allPapers is fresh here.
    trigger(zoteroImportCompleted, prevAutoScoreZoteroRef, true);
    trigger(bibtexImportCompleted, prevAutoScoreBibtexRef, true);
  }, [trawlCompleted, snowballCompleted, smartTrawlCompleted, zoteroImportCompleted, bibtexImportCompleted, conceptUnscoredIds, backgroundScoring, concept, atScoringLimit, startScoring]);

  // Recently-added highlight: arm a corpus baseline when any import/trawl
  // STARTS — trawls stream papers in progressively, so a completion-time
  // diff would miss everything that landed mid-run. The highlight set is
  // derived live as (current − baseline); it clears on chip dismiss, the
  // next import re-arming a fresh baseline, or switching concepts.
  const isTrawlingNow = useTrawlStore((s) => s.isTrawling);
  const isSnowballingNow = useSnowballStore((s) => s.isSnowballing);
  const isSpotTrawlingNow = useSmartTrawlStore((s) => s.isRunning);
  const zoteroStatus = useZoteroBulkImportStore((s) => s.status);
  const zoteroImportActive = zoteroStatus === "pending" || zoteroStatus === "running";
  const bibtexImporting = useBibtexImportStore((s) => s.isImporting);
  const prevArmRefs = useRef({ trawl: false, snowball: false, spot: false, zotero: false, bibtex: false });
  useEffect(() => {
    const armIfStarted = (
      running: boolean,
      key: keyof typeof prevArmRefs.current,
      source: string,
    ) => {
      if (running && !prevArmRefs.current[key]) {
        // Papers query still loading → baseline would be spuriously empty
        // and EVERY paper would flash. Leave the edge unrecorded so a later
        // run (papers loaded, still running) arms correctly.
        if (trawledLoading) return;
        useRecentlyAddedStore.getState().arm(conceptId, allPapers.map((p) => p.id), source);
      }
      prevArmRefs.current[key] = running;
    };
    armIfStarted(isTrawlingNow, "trawl", "Trawl");
    armIfStarted(isSnowballingNow, "snowball", "Snowball");
    armIfStarted(isSpotTrawlingNow, "spot", "Spot Trawl");
    armIfStarted(zoteroImportActive, "zotero", "Zotero import");
    armIfStarted(bibtexImporting, "bibtex", "BibTeX import");
  }, [isTrawlingNow, isSnowballingNow, isSpotTrawlingNow, zoteroImportActive, bibtexImporting, allPapers, conceptId, trawledLoading]);

  const raConceptId = useRecentlyAddedStore((s) => s.conceptId);
  const raBaselineIds = useRecentlyAddedStore((s) => s.baselineIds);
  const raDismissed = useRecentlyAddedStore((s) => s.dismissed);
  const raSource = useRecentlyAddedStore((s) => s.source);
  const recentlyAddedIds = useMemo(
    () =>
      deriveRecentlyAddedIds(
        { conceptId: raConceptId, baselineIds: raBaselineIds, dismissed: raDismissed },
        conceptId,
        allPapers.map((p) => p.id),
      ),
    [raConceptId, raBaselineIds, raDismissed, conceptId, allPapers],
  );
  // "N scoring…" chip suffix — recomputes live as scores land.
  const recentlyAddedScoringCount = useMemo(() => {
    if (recentlyAddedIds.size === 0) return 0;
    let n = 0;
    for (const p of allPapers) {
      if (recentlyAddedIds.has(p.id) && p.relevance_score === 0) n++;
    }
    return n;
  }, [recentlyAddedIds, allPapers]);

  // Dismissing the chip also retires the added_at sort IF one of our own
  // "surface the newest" actions set it — the table returns to the default
  // Score sort. A sort the user picked some other way is left alone.
  const handleDismissRecentlyAdded = useCallback(() => {
    useRecentlyAddedStore.getState().dismiss();
    if (search.sortField === "added_at") {
      navigate({ search: { ...search, sortField: undefined, sortDirection: undefined } });
    }
  }, [navigate, search]);


  // Compute idle PDF count from filtered papers
  const idlePdfCount = useMemo(
    () => filtered.filter((p) => p.canonical_pdf_status === "Idle").length,
    [filtered],
  );

  // Compute missing abstract count (use allPapers — concept-level operation)
  const missingAbstractCount = useMemo(
    () => allPapers.filter((p) => !p.abstract && p.abstract_status !== "fetched").length,
    [allPapers],
  );

  const handleFiltersChange = useCallback(
    (newFilters: PaperFilters) => {
      // Mutex: changing filter chips means the user is leaving scope mode.
      // setStoredFilters' spread preserves paperIdScope* otherwise, which
      // would let scope coexist with filters and contradict the documented
      // mutual-exclusion invariant.
      if (paperIdScope) clearPaperIdScope(conceptId);
      setStoredFilters(conceptId, { ...newFilters, angle: undefined }, sortField, sortDirection, search.wsId);
      navigate({
        search: {
          searchText: newFilters.searchText,
          minCitations: newFilters.minCitations,
          earliestYear: newFilters.earliestYear,
          isInfluential: newFilters.isInfluential || undefined,
          minScore: newFilters.minScore,
          contributionType: newFilters.contributionType,
          argumentationRole: newFilters.argumentationRole,
          overrideFilter: newFilters.overrideFilter,
          readingStatus: newFilters.readingStatus,
          hasEvidence: newFilters.hasEvidence || undefined,
          hasStashed: newFilters.hasStashed || undefined,
          tagIds: newFilters.tagIds,
          showDismissed: newFilters.showDismissed || undefined,
          dismissedView: newFilters.dismissedView || undefined,
          showFlagged: newFilters.showFlagged || undefined,
          flaggedView: newFilters.flaggedView || undefined,
          sourceFilter: newFilters.sourceFilter,
          addedWithin: newFilters.addedWithin,
          viaSeed: newFilters.viaSeed,
          angle: newFilters.angle,
          monitorInboxView: newFilters.monitorInboxView || undefined,
          wsId: search.wsId,
          sortField,
          sortDirection: sortField ? sortDirection : undefined,
        },
      });
    },
    [navigate, conceptId, setStoredFilters, sortField, sortDirection, search.wsId, paperIdScope, clearPaperIdScope],
  );

  // "Surface the newest papers" (chip Sort-by-newest): sorting to added_at
  // desc is a NO-OP once that sort is already active (the filter-store
  // mirrors it). The scroll tick makes the action observable regardless:
  // the table jumps to the top row, where the newest papers sit under this
  // sort (2026-07-17 re-report: "Sort by newest doesn't do anything").
  const [tableScrollTick, setTableScrollTick] = useState(0);
  const handleSortByNewest = useCallback(() => {
    navigate({
      search: { ...search, addedWithin: undefined, sortField: "added_at", sortDirection: "desc" },
    });
    setTableScrollTick((t) => t + 1);
  }, [navigate, search]);

  const handleAngleSelect = useCallback(
    (angle: string | undefined) => navigate({ search: { ...search, angle } }),
    [navigate, search],
  );

  const handleSortChange = useCallback(
    (newSortField: SortField | undefined, newSortDirection: SortDirection) => {
      setStoredFilters(conceptId, storeFilters, newSortField, newSortDirection, search.wsId);
      navigate({
        search: {
          ...search,
          sortField: newSortField,
          sortDirection: newSortField ? newSortDirection : undefined,
        },
      });
    },
    [navigate, conceptId, setStoredFilters, storeFilters, search],
  );

  const handleTrawl = useCallback(() => {
    if (foundationalPapers.length > 0) {
      trawl(foundationalPapers);
    }
  }, [foundationalPapers, trawl]);

  const handleImportBibTeX = useCallback(() => bibFileRef.current?.click(), []);
  const handleBibFileSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        uploadAndImport(file);
        e.target.value = "";
      }
    },
    [uploadAndImport],
  );


  const handleSearch = useCallback(
    async (query: string, limit = 50) => {
      setIsSearching(true);
      try {
        const outcome = await runCurateSearch({
          conceptId,
          query,
          limit,
          previewEnabled: searchPreviewEnabled,
          addPapers: (papers) =>
            addPapers.mutate({ papers, role: "trawled", added_source: "search" }),
        });
        if (outcome.kind === "empty") {
          toast.info(outcome.title, { description: outcome.description });
        } else if (outcome.kind === "preview") {
          setSearchPreview((prev) => ({
            seq: (prev?.seq ?? 0) + 1, conceptId, query, response: outcome.response,
          }));
        }
      } catch (err) {
        // The legacy path never caught (errors surfaced unhandled, as before).
        if (!searchPreviewEnabled) throw err;
        toast.error("Search failed", { description: await apiErrorDetail(err, "Please try again.") });
      } finally {
        setIsSearching(false);
      }
    },
    [addPapers, conceptId, searchPreviewEnabled],
  );

  const handlePromoteSelected = useCallback(() => {
    const ids = [...selectedIds];
    if (ids.length > 0) {
      promoteMutation.mutate(ids, {
        onSuccess: () => deselectAll(),
      });
    }
  }, [selectedIds, promoteMutation, deselectAll]);

  const handleSelectAll = useCallback(() => {
    selectAll(filtered.map((p) => p.id));
  }, [filtered, selectAll]);

  const handleSelectionChange = useCallback(
    (ids: string[]) => {
      selectAll(ids);
    },
    [selectAll],
  );

  const handlePromoteSingle = useCallback(
    async (paperId: string) => {
      await promoteMutation.mutateAsync([paperId]);
      setSelectedPaper(null);
    },
    [promoteMutation],
  );

  // Scoring handlers — target filtered (visible) papers only
  const handleScoreAll = useCallback(() => {
    if (atScoringLimit) return;
    if (unscoredIds.length > 0) {
      startScoring({ paperIds: unscoredIds, mode: "standard" });
    }
  }, [startScoring, unscoredIds, atScoringLimit]);

  // ── Workflow advisor signals + Next step action handlers ───────────────
  // All three triggers fire off observable state. refine_after_edit uses a
  // SOUND staleness signal: backend last_definition_edit_at =
  // MAX(concept_versions.changed_at), which (unlike concept.updated_at) only
  // advances on real definition edits, not background writes. A scored paper is
  // stale iff it was scored before that timestamp.
  const staleScoredIds = useMemo(
    () => staleScoredPaperIds(allPapers, concept?.last_definition_edit_at ?? null),
    [allPapers, concept?.last_definition_edit_at],
  );
  const staleScoredCount = staleScoredIds.length;

  const topPaper = useMemo(() => {
    let best: PaperWithRole | null = null;
    for (const p of allPapers) {
      if (
        (p.relevance_score ?? 0) > 0 &&
        (!best || (p.relevance_score ?? 0) > (best.relevance_score ?? 0))
      ) {
        best = p;
      }
    }
    return best;
  }, [allPapers]);

  const hasFieldDebate = !!debateMap;
  const campSurfaced = (debateMap?.camps?.length ?? 0) > 0;
  // ── Workflow advisor: single proactive pointer card ────────────────────
  // The advisor (computeRecommendations) replaces the old trigger queue: it
  // ranks the whole workflow and we surface only its top recommendation as a
  // pointer into the "Where am I?" map panel.
  const curateSnapshot = useMemo(
    () =>
      toSnapshot({
        counts: {
          foundational: foundationalPapers.length,
          trawled: allPapers.length,
          scored: scoredCount,
          with_pdfs: 0,
        },
        cycle: cycleSignals ?? EMPTY_CYCLE_SIGNALS,
        concept: {
          abstract: concept?.abstract ?? "",
          abstract_auto_drafted: concept?.abstract_auto_drafted,
          argument_analysis: concept?.argument_analysis ?? null,
          last_definition_edit_at: concept?.last_definition_edit_at ?? null,
        },
        hasFieldDebate,
        campSurfaced,
        isPaid: isPaidPlan(currentUser?.plan),
        isScoring: isScoringNow || backgroundScoring,
        atScoringLimit,
        hasTopPaper: !!topPaper,
        topPaperOpened: topPaper != null && selectedPaper?.id === topPaper.id,
        // Reviewed = a decision was made (rate/note/highlight/judged dismissal
        // /skim/read) — not engagement-only like readOrSkimmedCount above.
        reviewedCount: allPapers.filter(isReviewed).length,
        unscoredCount: conceptUnscoredCount,
        papersOutOfBoundary: staleScoredCount,
        conceptEdited: !!concept?.last_definition_edit_at,
        hasDraft: false,
        untriagedCount: allPapers.filter(
          (p) =>
            (p.added_source === "monitor" || p.added_source === "trawl_references") &&
            !p.triaged &&
            !p.exclusion_reason,
        ).length,
      }),
    [
      foundationalPapers.length,
      allPapers,
      scoredCount,
      cycleSignals,
      concept?.abstract,
      concept?.abstract_auto_drafted,
      concept?.argument_analysis,
      concept?.last_definition_edit_at,
      hasFieldDebate,
      campSurfaced,
      currentUser?.plan,
      isScoringNow,
      backgroundScoring,
      atScoringLimit,
      topPaper,
      selectedPaper?.id,
      conceptUnscoredCount,
      staleScoredCount,
    ],
  );

  const topRec = useMemo(
    () => computeRecommendations(curateSnapshot).find((r) => r.status !== "blocked") ?? null,
    [curateSnapshot],
  );

  // ── Frontier wiring ──────────────────────────────────────────────────────
  // Driven by the advisor's top recommendation so the ➤ button and the
  // "What's next?" map panel can never disagree.
  // onFrontier is declared after handleRescoreFullText to avoid a forward-ref.
  // While a scoring op is in flight, the OperationBar already shows progress;
  // suppress the workflow nudge so it doesn't say "Score N" over active scoring.
  const frontier = useMemo(
    () =>
      topRec && !isScoringNow
        ? { label: topRec.title, accent: "green" as const, reason: topRec.rationale }
        : null,
    [topRec, isScoringNow],
  );

  // YOU ARE HERE for the "Where am I?" overlay is now state-driven from
  // usePapersCount inside WhereAmIOverlay itself — no per-page recording
  // needed. The previous history-based approach persisted stale selectedPaper
  // across concept switches and wrote "review" to fresh concepts' storage.

  // Expose current guide phase on document.body so <FeatureRef> can route
  // clicks to the right surface (JourneyBarOnboarding event vs. drawer).
  useEffect(() => {
    document.body.setAttribute("data-guide-phase", guide.phase);
    return () => {
      document.body.removeAttribute("data-guide-phase");
    };
  }, [guide.phase]);

  // No atScoringLimit guard: full-text rescore targets already-scored
  // papers, which the backend quota gate exempts (Codex MED 2026-07-14 —
  // the FE block stranded at-limit users who only wanted a rescore).
  const handleRescoreFullText = useCallback(() => {
    if (needsRescoreIds.length > 0) {
      startScoring({ paperIds: needsRescoreIds, mode: "fulltext_rescore" });
    }
  }, [startScoring, needsRescoreIds]);

  // Declared here (after handleRescoreFullText) to avoid a forward-reference error.
  const onFrontier = useCallback(() => {
    if (!topRec) return;
    switch (topRec.id) {
      // Empty concept: the rec's action is just CURATE (the page we're already
      // on), so a plain navigate is a no-op. Fire seed discovery directly —
      // that IS the "add seed papers" next step.
      case "add_seeds": discover(); break;
      case "trawl": setTrawlClicked(true); handleTrawl(); break;
      // Score the SAME set the rec's label counts (concept-level, unfiltered)
      // — handleScoreAll scores the filtered view, so with a camp scope or
      // filter active the visible set could be empty and the click a no-op.
      case "score_imports":
        setScoreClicked(true);
        if (!atScoringLimit && conceptUnscoredIds.length > 0) {
          startScoring({ paperIds: conceptUnscoredIds, mode: "standard" });
        }
        break;
      case "rescore": handleRescoreFullText(); break;
      case "review": if (topPaper) setSelectedPaper(topPaper); break;
      default:
        crossNavigate({ to: topRec.action.to, params: { conceptId }, search: topRec.action.search ?? {} } as never);
    }
  }, [topRec, discover, handleTrawl, startScoring, conceptUnscoredIds, atScoringLimit, handleRescoreFullText, topPaper, setSelectedPaper, setTrawlClicked, setScoreClicked, crossNavigate, conceptId]);

  // No atScoringLimit guard: an all-scored selection is a quota-exempt
  // rescore; a mixed selection gets capped (or 403'd with the server's
  // self-explanatory copy) by the backend — the enforcer since 2026-07-14.
  const handleScoreSelected = useCallback(() => {
    const ids = [...selectedIds];
    if (ids.length > 0) {
      startScoring({ paperIds: ids, rescore: true });
    }
  }, [selectedIds, startScoring]);

  const resetScoresMutation = useMutation({
    mutationFn: () =>
      apiClient
        .post(`api/concepts/${conceptId}/papers/reset-scores`)
        .json<{ reset: number }>(),
    onSuccess: (data: { reset: number }) => {
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
      toast.success(`Reset scores for ${data.reset} papers — use Score to re-score`);
    },
  });

  // PDF retrieval handlers
  const handleDownloadPdfs = useCallback(() => {
    const ids = filtered
      .filter((p) => p.canonical_pdf_status === "Idle")
      .map((p) => p.id);
    if (ids.length > 0) {
      startRetrieval({ paperIds: ids });
    }
  }, [filtered, startRetrieval]);

  const handleDownloadSelectedPdfs = useCallback(() => {
    const ids = [...selectedIds];
    if (ids.length > 0) {
      startRetrieval({ paperIds: ids });
    }
  }, [selectedIds, startRetrieval]);

  const handleDownloadSingle = useCallback(
    (paperId: string) => {
      // Optimistic: immediately show "Fetching" in detail pane
      if (selectedPaper?.id === paperId) {
        setSelectedPaper({ ...selectedPaper, canonical_pdf_status: "Fetching" });
      }
      startRetrieval({ paperIds: [paperId], force: true });
    },
    [startRetrieval, selectedPaper],
  );

  const handleViewPdf = useCallback(
    async (paperId: string) => {
      trackEvent("feature_use", { feature: "pdf_view", action: "started" });

      const markIfFirstOpen = () => {
        if (!openedPdfPapersRef.current.has(paperId)) {
          openedPdfPapersRef.current.add(paperId);
          guideSignals.increment(conceptId, 'pdfOpenCount');
        }
      };

      // If PDF is already loaded for this paper, just switch back to pdf view
      if (pdfUrl && selectedPaper?.id === paperId) {
        setPdfViewMode("pdf");
        markIfFirstOpen();
        return;
      }
      try {
        const result = await getDownloadUrl(paperId);
        if (result) {
          setPdfUrl(result.url);
          setPdfPaperId(paperId);
          setPdfSource(result.source);
          setPdfViewMode("pdf");
          markIfFirstOpen();
        } else {
          toast.error("Could not get PDF download URL");
        }
      } catch {
        toast.error("Failed to load PDF", {
          description: "The PDF may not be available. Try downloading it again.",
        });
      }
    },
    [getDownloadUrl, pdfUrl, selectedPaper?.id, conceptId],
  );

  const handleExpandPdf = useCallback(
    async (paperId: string) => {
      trackEvent("feature_use", { feature: "pdf_view", action: "expanded" });
      // If PDF is already loaded for this paper, just maximize
      if (pdfUrl && selectedPaper?.id === paperId) {
        setPdfViewMode("pdf");
        setPdfMaximized(true);
        return;
      }
      try {
        const result = await getDownloadUrl(paperId);
        if (result) {
          setPdfUrl(result.url);
          setPdfPaperId(paperId);
          setPdfSource(result.source);
          setPdfViewMode("pdf");
          setPdfMaximized(true);
        } else {
          toast.error("Could not get PDF download URL");
        }
      } catch {
        toast.error("Failed to load PDF", {
          description: "The PDF may not be available. Try downloading it again.",
        });
      }
    },
    [getDownloadUrl, pdfUrl, selectedPaper?.id],
  );

  const handleViewFullText = useCallback(
    async (paperId: string) => {
      try {
        const res = await apiClient
          .get(`api/concepts/${conceptId}/pdf/full-text/${paperId}`)
          .json<{ text: string; title: string }>();
        // Guard against stale completions — only apply if same paper is still selected
        if (prevPaperId.current !== paperId) return;
        setFullText(res.text);
        setPdfViewMode("fulltext");
      } catch {
        toast.error("Failed to load full text");
      }
    },
    [conceptId],
  );

  const handleExtractAbstract = useCallback(
    async (paperId: string) => {
      const res = await extractAbstract(paperId);
      if (res?.abstract && selectedPaper?.id === paperId) {
        setSelectedPaper({
          ...selectedPaper,
          abstract: res.abstract,
          abstract_status: "fetched",
        });
      } else if (res && !res.abstract && selectedPaper?.id === paperId) {
        setSelectedPaper({
          ...selectedPaper,
          abstract_status: "failed",
        });
      }
    },
    [extractAbstract, selectedPaper],
  );

  // Stash the selected file and open the attestation dialog. Actual upload
  // runs only after the user confirms rights attestation.
  const handleUploadPdf = useCallback(
    (paperId: string, file: File) => {
      setUploadAttested(false);
      setPendingUpload({ paperId, file });
    },
    [],
  );

  const confirmAttestedUpload = useCallback(() => {
    if (!pendingUpload || !uploadAttested) return;
    const { paperId, file } = pendingUpload;
    setPendingUpload(null);
    setUploadAttested(false);
    uploadPdf(paperId, file, true);
  }, [pendingUpload, uploadAttested, uploadPdf]);

  const cancelAttestedUpload = useCallback(() => {
    setPendingUpload(null);
    setUploadAttested(false);
  }, []);

  const handleScoreOverride = useCallback(
    (paperId: string, score: number | null) => {
      // Optimistic detail pane update
      if (selectedPaper?.id === paperId) {
        setSelectedPaper({
          ...selectedPaper,
          user_score_override: score === 0 || score === null ? null : score,
        });
      }
      scoreOverrideMutation.mutate(
        { paperId, score: score ?? 0 },
        {
          onSuccess: (updated) => {
            // Never store the whole response: it carries angle_scores: null.
            setSelectedPaper((cur) => mergeScoreOverride(cur, updated));
            guideSignals.mark(conceptId, "hasOverriddenScore");
          },
        },
      );
    },
    [scoreOverrideMutation, selectedPaper, conceptId],
  );

  const handleClosePdfViewer = useCallback(() => {
    setPdfViewMode("info");
    // Keep pdfUrl so PdfViewer stays mounted (hidden) — reopening is instant
    setFullText(null);
    setPdfMaximized(false);
  }, []);

  const pdfPaperLabel = useMemo(() => {
    if (!selectedPaper) return undefined;
    const first = selectedPaper.authors?.[0];
    const last = first ? first.trim().split(/\s+/).pop() : "Unknown";
    const suffix = (selectedPaper.authors?.length ?? 0) > 1 ? " et al." : "";
    const year = selectedPaper.year ? `, ${selectedPaper.year}` : "";
    return `${last}${suffix}${year}`;
  }, [selectedPaper]);

  // Auto-load PDF when selected paper changes while PDF view is active
  const prevPaperId = useRef<string | null>(null);
  useEffect(() => {
    const paperId = selectedPaper?.id ?? null;
    if (paperId === prevPaperId.current) return;
    prevPaperId.current = paperId;

    // Reset page to 1 for new paper
    setPdfCurrentPage(1);
    // Clear stale content when paper changes
    setPdfUrl(null);
    setPdfPaperId(null);
    setPdfNumPages(null);
    setFullText(null);
    // Summary source is per-paper; clear so stale source from the previous
    // paper doesn't wrongly gate the Regenerate-from-upload button.
    setSummarySource(undefined);

    if (!selectedPaper || pdfViewMode !== "pdf") return;

    // If paper has a PDF (user upload OR canonical), load it; otherwise fall
    // back to info view. User uploads don't set canonical_pdf_status so we
    // also trust pdf_available_source from the resolver.
    const hasResolvedPdf =
      selectedPaper.pdf_available_source === "user" ||
      selectedPaper.pdf_available_source === "canonical";
    const hasCanonicalPdf =
      selectedPaper.canonical_pdf_status === "Available" &&
      !!selectedPaper.canonical_pdf_storage_key;
    if (hasResolvedPdf || hasCanonicalPdf) {
      const loadedForId = selectedPaper.id;
      getDownloadUrl(loadedForId).then((result) => {
        if (result) {
          setPdfUrl(result.url);
          setPdfPaperId(loadedForId);
          setPdfSource(result.source);
        } else {
          setPdfViewMode("info");
          setPdfUrl(null);
          setPdfPaperId(null);
          setPdfSource("none");
        }
      }).catch(() => {
        setPdfViewMode("info");
        setPdfUrl(null);
        setPdfPaperId(null);
        setPdfSource("none");
      });
    } else {
      setPdfViewMode("info");
      setPdfUrl(null);
      setPdfPaperId(null);
      setPdfSource("none");
    }
  }, [selectedPaper?.id, selectedPaper?.canonical_pdf_status, selectedPaper?.canonical_pdf_storage_key, selectedPaper?.pdf_available_source, pdfViewMode, getDownloadUrl]);

  const handleExportCSV = useCallback(() => {
    if (filtered.length > 0) {
      exportPapersToCSV(filtered, "paperchase-expand-papers.csv");
    }
  }, [filtered]);

  const handleExportBibTeX = useCallback(() => {
    if (filtered.length > 0) {
      const name = concept?.name ?? "papers";
      exportPapersToBibTeX(filtered, `${name}-references.bib`);
    }
  }, [filtered, concept]);

  // PDF text selection handlers
  const handleAppendToAbstract = useCallback(
    (text: string) => {
      if (!concept) return;
      const separator = concept.abstract ? "\n\n" : "";
      updateConceptMutation.mutate(
        { abstract: (concept.abstract || "") + separator + text },
        {
          onSuccess: () => toast.success("Text appended to concept abstract"),
          onError: () => toast.error("Failed to append text to abstract"),
        },
      );
    },
    [concept, updateConceptMutation],
  );

  const handleAddAsChunk = useCallback(
    (text: string) => {
      if (!selectedPaper) return;
      addExcerptMutation.mutate(
        { paperId: selectedPaper.id, data: { text } },
        {
          onSuccess: () => toast.success("Saved to Corpus Chat"),
          onError: () => toast.error("Failed to add excerpt"),
        },
      );
    },
    [selectedPaper, addExcerptMutation],
  );

  // Summarize handler
  const handleSummarize = useCallback(
    (paperId: string) => {
      summarizeMutation.mutate(paperId, {
        onSuccess: (result) => {
          if (result.error) {
            toast.error(result.error);
          } else if (selectedPaper?.id === paperId) {
            setSelectedPaper({ ...selectedPaper, ai_summary: result.summary });
            if (result.source === "user" || result.source === "canonical") {
              setSummarySource(result.source);
            }
          }
        },
        onError: () => toast.error("Summarization failed"),
      });
    },
    [summarizeMutation, selectedPaper],
  );

  // Re-summarize using the user's uploaded PDF. Backend automatically prefers
  // the user's PaperPdf when one exists, so this just triggers a fresh
  // summarize request — the response source will flip to "user".
  const handleRegenerateFromUpload = useCallback(
    (paperId: string) => handleSummarize(paperId),
    [handleSummarize],
  );

  // Explain handler
  const handleExplain = useCallback(
    (text: string) => {
      if (!selectedPaper) return;
      setExplanation({ text, result: "" });
      explainMutation.mutate(
        { paperId: selectedPaper.id, text },
        {
          onSuccess: (r) => {
            if (r.error) {
              toast.error("Explain failed");
              setExplanation(null);
            } else {
              setExplanation({ text, result: r.explanation });
            }
          },
          onError: () => {
            toast.error("Explain failed");
            setExplanation(null);
          },
        },
      );
    },
    [selectedPaper, explainMutation],
  );

  // PDF evidence stash — persist a PaperHighlight (source="evidence") at stash
  // time so the excerpt renders on the PDF canvas. The tray references the
  // resulting highlight by id. See docs/superpowers/plans/2026-04-19-evidence-attach-rects.md.
  //
  // Duplicate-row note: the toolbar's onDismiss + getSelection().removeAllRanges()
  // after the first click means a double-click on the same Evidence button
  // cannot fire twice (the second click has no selection — helper returns
  // null). Genuine duplicates only arise if the user re-selects the SAME
  // text and re-clicks Evidence while the first POST is still in flight —
  // an uncommon, low-harm case. Proper fix is server-side
  // UNIQUE(user, paper, text, page); tracked as follow-up.
  const handlePdfStashEvidence = useCallback(
    async (data: { text: string; page: number; rects: Array<{ x: number; y: number; width: number; height: number; pageIndex: number }> | null }) => {
      if (!selectedPaper) return;
      try {
        // concept_id omitted — evidence highlights must NOT auto-populate
        // selected_highlight_ids (plan §Behavior Decisions #2).
        const highlight = await apiClient
          .post(`api/papers/${selectedPaper.id}/highlights`, {
            json: {
              text: data.text,
              page: data.page,
              rects: data.rects,
              source: "evidence",
            },
          })
          .json<PaperHighlight>();

        insertHighlightInCache(queryClient, selectedPaper.id, highlight);

        useEvidenceStore.getState().addPending({
          text: data.text,
          paperId: selectedPaper.id,
          paperTitle: selectedPaper.title,
          pageNumber: data.page + 1, // 0-based PdfRect → 1-based PendingEvidence
          highlightId: highlight.id,
          rects: data.rects ?? undefined,
        });
        toast.success("Evidence saved", {
          action: {
            label: "Go to Evidence →",
            onClick: () => navigate({
              to: "/concepts/$conceptId/build",
              params: { conceptId },
            }),
          },
        });
      } catch {
        toast.error("Failed to save evidence");
      }
    },
    [selectedPaper, queryClient, navigate, conceptId],
  );

  // HTML evidence stash — preserves today's behavior exactly: store-only, no
  // PaperHighlight row is created. Splitting the handler keeps HTML behavior
  // byte-for-byte while the PDF path gains rect-persistence.
  const handleHtmlStashEvidence = useCallback(
    (text: string) => {
      if (!selectedPaper) return;
      useEvidenceStore.getState().addPending({
        text,
        paperId: selectedPaper.id,
        paperTitle: selectedPaper.title,
        // highlightId intentionally omitted — no highlight is created for HTML.
      });
      toast.success("Evidence saved", {
        action: {
          label: "Go to Evidence →",
          onClick: () => navigate({
            to: "/concepts/$conceptId/build",
            params: { conceptId },
          }),
        },
      });
    },
    [selectedPaper, navigate, conceptId],
  );

  // Bulk evidence stash — stashes all selected highlights as pending evidence
  const handleBulkStashEvidence = useCallback(() => {
    if (!selectedPaper || !highlights.length) return;
    const selected = highlights.filter((h) => selectedHighlightIds.includes(h.id));
    if (selected.length === 0) return;
    const store = useEvidenceStore.getState();
    for (const h of selected) {
      store.addPending({
        text: h.text,
        paperId: selectedPaper.id,
        paperTitle: selectedPaper.title,
        // h.page is 0-based; PendingEvidence.pageNumber is 1-based.
        pageNumber: h.page != null ? h.page + 1 : undefined,
        highlightId: h.id,
        rects: Array.isArray(h.rects)
          ? (h.rects as Array<{ x: number; y: number; width: number; height: number; pageIndex: number }>)
          : undefined,
      });
    }
    toast.success(`${selected.length} excerpt${selected.length > 1 ? "s" : ""} stashed as evidence`, {
      action: {
        label: "View →",
        onClick: () => navigate({ to: "/concepts/$conceptId/build", params: { conceptId } }),
      },
    });
  }, [selectedPaper, highlights, selectedHighlightIds, conceptId, navigate]);

  // Reading status handler — optimistic detail pane update
  const selectedPaperRef = useRef(selectedPaper);
  selectedPaperRef.current = selectedPaper;
  // Dedup tracker for pdfOpenCount — ensures we count each unique paper opened per session
  const openedPdfPapersRef = useRef<Set<string>>(new Set());

  const handleReadingStatus = useCallback(
    (paperId: string, status: string) => {
      const current = selectedPaperRef.current;
      if (current?.id === paperId) {
        setSelectedPaper({ ...current, reading_status: status });
      }
      readingStatusMutation.mutate({ paperId, status });
    },
    [readingStatusMutation],
  );

  // Notes handler
  const handleNotesChange = useCallback(
    (paperId: string, notes: string) => {
      notesMutation.mutate(
        { paperId, notes },
        {
          onSuccess: (updated) => {
            if (selectedPaper?.id === paperId) {
              setSelectedPaper({
                ...selectedPaper,
                notes: updated.notes,
              });
            }
          },
        },
      );
    },
    [notesMutation, selectedPaper],
  );

  // Tag handlers
  const handleUpdatePaperTags = useCallback(
    (paperId: string, tagIds: string[]) => {
      const current = selectedPaperRef.current;
      if (current?.id === paperId) {
        const allTags = tagsData ?? [];
        const newTags = tagIds
          .map((id) => allTags.find((t) => t.id === id))
          .filter((t): t is NonNullable<typeof t> => t != null);
        setSelectedPaper({ ...current, tags: newTags });
      }
      updatePaperTagsMutation.mutate(
        { paperId, tagIds },
        { onSuccess: () => guideSignals.mark(conceptId, 'hasTaggedOrNotedPaper') },
      );
    },
    [updatePaperTagsMutation, tagsData],
  );

  const handleCreateTag = useCallback(
    (data: { name: string; color: string }) => {
      const paper = selectedPaperRef.current;
      // Show tag on paper immediately (optimistic)
      if (paper) {
        const optimisticTag = { id: `temp-${Date.now()}`, name: data.name, color: data.color, paper_count: 0 };
        setSelectedPaper({ ...paper, tags: [...(paper.tags ?? []), optimisticTag] });
      }
      createTagMutation.mutate(data, {
        onSuccess: (newTag) => {
          // Auto-assign the real tag to the current paper
          const current = selectedPaperRef.current;
          if (current) {
            // Replace temp tag with real one in detail pane
            const updatedTags = current.tags.map((t) =>
              t.id.startsWith("temp-") && t.name === newTag.name ? newTag : t,
            );
            setSelectedPaper({ ...current, tags: updatedTags });
            // Persist assignment to server
            updatePaperTagsMutation.mutate(
              {
                paperId: current.id,
                tagIds: updatedTags.map((t) => t.id),
              },
              { onSuccess: () => guideSignals.mark(conceptId, 'hasTaggedOrNotedPaper') },
            );
          }
        },
      });
    },
    [createTagMutation, updatePaperTagsMutation],
  );

  return (
    <div className="space-y-3">
      {/* Camp filter banner */}
      {campLabel && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-blue-950/30 border border-blue-500/30 rounded-lg text-xs">
          <span className="text-blue-300">
            Showing papers from camp: <strong>{campLabel}</strong>
          </span>
          <button
            onClick={clearCampFilter}
            className="text-blue-400 hover:text-blue-300 underline"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* Hidden file input for BibTeX import */}
      <input ref={bibFileRef} type="file" accept=".bib" className="hidden" onChange={handleBibFileSelected} />

      {/* ActionBar — unified Find / Score / Corpus / Monitor + frontier ➤ */}
      <ActionBar
        frontier={frontier}
        onFrontier={onFrontier}
        onFrontierWhy={() => useWhereAmIStore.getState().open()}
        papers={allPapers}
        busy={trawlInFlight || scoringInFlight || isRetrieving || snowballInFlight || isSearching}
        busyJob={
          trawlInFlight ? "a trawl" : scoringInFlight ? "scoring" : isRetrieving ? "PDF retrieval"
            : snowballInFlight ? "a snowball" : isSearching ? "a search" : undefined
        }
        isSearching={isSearching}
        onSearchS2={handleSearch}
        searchPlaceholder={
          // Neutral: the flag doesn't say which sources are on; the dialog does.
          searchPreviewEnabled ? "Search papers…" : undefined
        }
        find={{
          hasFoundationalPapers: foundationalPapers.length > 0,
          onTrawl: () => { setTrawlClicked(true); handleTrawl(); },
          onSmartTrawl: () => smartTrawl.run({ scope: "foundational" }),
          onSnowball: () => startSnowball(allPapers),
          onImportPdfs: () => setShowBulkImport(true),
          onImportBibTeX: handleImportBibTeX,
          onZotero: () => useZoteroBulkImportModalStore.getState().open(conceptId),
          onRunMonitor: startMonitor,
          onStartOver: concept?.reset_enabled ? () => setShowStartOver(true) : undefined,
          onUndoReset:
            concept?.reset_enabled && concept.last_reset && !concept.last_reset.undone
              ? () =>
                  undoReset.mutate(concept.last_reset!.id, {
                    onSuccess: () => toast.success("Reset undone"),
                    onError: async (err) =>
                      toast.error(await apiErrorDetail(err, "Couldn't undo the reset")),
                  })
              : undefined,
          undoResetLabel: concept?.last_reset
            ? `Undo last reset (${new Date(concept.last_reset.created_at).toLocaleDateString()})`
            : undefined,
        }}
        score={{
          onScoreAll: () => { setScoreClicked(true); handleScoreAll(); },
          onScoreSelected: () => { setScoreClicked(true); handleScoreSelected(); },
          onRescoreFullText: handleRescoreFullText,
          onResetScores: () => resetScoresMutation.mutate(),
          onBackfillAbstracts: () => backfillAbstracts.mutate(),
          unscoredCount,
          needsRescoreCount,
          idlePdfCount,
          scoringLimit,
          scoredCount,
          isResetPending: resetScoresMutation.isPending,
          onUpgrade: () => crossNavigate({ to: "/settings/billing" }),
          missingAbstractCount,
          // The POST returns as soon as the job is queued; the run lasts as long
          // as the status poll (backfillProgress). isPending alone hid the
          // progress label and re-enabled the button mid-run.
          isBackfilling: backfillAbstracts.isPending || backfillProgress !== null,
          backfillProgress,
        }}
        corpus={{
          onExportCSV: handleExportCSV,
          onExportBibTeX: handleExportBibTeX,
          onDownloadPdfs: handleDownloadPdfs,
          onDownloadSelectedPdfs: handleDownloadSelectedPdfs,
        }}
        monitor={{
          concept: concept!,
          isMonitorRunning,
          monitorRecentCount: monitorRecent.length,
          monitorHighCount: monitorRecent.filter((p) => (p.user_score_override ?? p.normalized_score) >= 60).length,
          monitorMidCount: monitorRecent.filter((p) => { const s = p.user_score_override ?? p.normalized_score; return s >= 30 && s < 60; }).length,
          monitorLowCount: monitorRecent.filter((p) => (p.user_score_override ?? p.normalized_score) < 30).length,
          monitorLastRunCount: monitorLastRun.length,
          monitorLastRunHighCount: monitorLastRun.filter((p) => (p.user_score_override ?? p.normalized_score) >= 60).length,
          monitorLastRunMidCount: monitorLastRun.filter((p) => { const s = p.user_score_override ?? p.normalized_score; return s >= 30 && s < 60; }).length,
          monitorLastRunLowCount: monitorLastRun.filter((p) => (p.user_score_override ?? p.normalized_score) < 30).length,
          untriagedCount: allPapers.filter((p) => (p.added_source === "monitor" || p.added_source === "trawl_references") && !p.triaged && !p.exclusion_reason).length,
          onRunMonitor: startMonitor,
          onCancelMonitor: cancelMonitor,
          onOpenTriageInbox: () => navigate({ search: { searchText: search.searchText, monitorInboxView: true } }),
          onMonitorSettingsChange: (settings) => monitorSettingsMutation.mutate(settings),
          isMonitorSettingsUpdating: monitorSettingsMutation.isPending,
        }}
      />

      {concept?.abstract_auto_drafted && (
        <DraftedQuestionCallout
          abstract={concept.abstract}
          onEdit={() => setConceptEditorOpen(true)}
          onConfirm={() => confirmDrafted.mutate()}
        />
      )}
      {concept && (
        <Dialog open={conceptEditorOpen} onOpenChange={setConceptEditorOpen}>
          <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Concept</DialogTitle>
              <DialogDescription>Edit your research question — every paper is graded against it.</DialogDescription>
            </DialogHeader>
            <ConceptPopover
              concept={concept}
              paperCount={allPapers.length}
              seedCount={paperCounts?.foundational ?? 0}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Corpus seeding panel (new-user/empty, non-expert). The expert-empty case
          previously rendered a redundant dashed hint — the Seed Papers panel below
          already shows the empty state + add/discover/import actions, so it's dropped. */}
      {!trawledLoading && foundationalPapers.length === 0 && allPapers.length === 0 && guide.phase !== "expert" && seedingSkippedFor !== conceptId ? (
        <CorpusSeedingPanel
          onDiscover={discover}
          onImport={() => setShowBulkImport(true)}
          onZotero={() => useZoteroBulkImportModalStore.getState().open(conceptId)}
          onSkip={() => setSeedingSkippedFor(conceptId)}
          onSelectPath={guide.setEntryPath}
          isDiscovering={seedDiscoveryRunning}
        />
      ) : null}


      {angles.enabled && concept && (
        <AngleManager
          concept={concept}
          conceptId={conceptId}
          facets={angles.facets}
          abstract={concept.abstract}
          keywords={concept.keywords}
          conceptScope={concept.scope}
          papers={angleBase}
          seeds={foundationalPapers}
          selected={validAngle(search.angle, angles.facets)}
          onSelect={handleAngleSelect}
          failed={!!concept.angle_rescore_failed}
          maxFacets={currentUser?.plan_limits?.max_facets}
          onTrawl={(s) => trawl(s)}
          trawling={trawlInFlight}
        />
      )}

      {/* Filter bar (show when we have papers) — flush lens row, no top margin */}
      {allPapers.length > 0 && (
        <div className="rounded-t-md border border-b-0 bg-muted/30">
        <FilterBar
          filters={filters}
          onChange={handleFiltersChange}
          seeds={seedOptions}
          papers={papers}
          totalCorpusCount={allPapers.length}
          workingSets={workingSets}
          activeWsId={wsId}
          onWsChange={(newWsId) => {
            if (paperIdScope) clearPaperIdScope(conceptId);
            setStoredFilters(conceptId, storeFilters, sortField, sortDirection, newWsId);
            navigate({ search: { ...search, wsId: newWsId } });
          }}
          onCreateWs={(name) => {
            wsActions.createWorkingSet.mutate({ name }, {
              onSuccess: (ws) => {
                // Seed with current filtered papers
                const paperIds = filtered.map((p) => p.id);
                if (paperIds.length > 0) {
                  wsActions.seedFromFilter.mutate({
                    wsId: ws.id,
                    paperIds,
                    filter: filters as Record<string, unknown>,
                  });
                }
                navigate({ search: { ...search, wsId: ws.id } });
              },
            });
          }}
          onWsDelete={(deletedWsId) => {
            // If we're on the WS being deleted, drop back to Full Corpus first
            // so the slim list doesn't briefly try to filter against a gone WS.
            if (search.wsId === deletedWsId) {
              setStoredFilters(conceptId, storeFilters, sortField, sortDirection, undefined);
              navigate({ search: { ...search, wsId: undefined } });
            }
            wsActions.deleteWorkingSet.mutate(deletedWsId, {
              onError: (e) =>
                toast.error(`Couldn't delete working set: ${(e as Error).message}`),
            });
          }}
          selectedCount={selectedIds.size}
          onCreateWsFromSelected={(name) => {
            const ids = Array.from(useSelectionStore.getState().selectedIds);
            wsActions.createWorkingSet.mutate({ name }, {
              onSuccess: (ws) => {
                wsActions.addPapers.mutate({ wsId: ws.id, paperIds: ids });
                navigate({ search: { ...search, wsId: ws.id } });
                useSelectionStore.getState().deselectAll();
              },
            });
          }}
          filteredCount={filtered.length}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          onReset={() => {
            if (paperIdScope) clearPaperIdScope(conceptId);
            setStoredFilters(conceptId, {}, "relevance_score", "desc", search.wsId);
            navigate({ search: {} });
          }}
          tags={tagsData}
          presets={presetsData}
          onSavePreset={(name, isDefault) => {
            createPreset.mutate({
              name,
              filters,
              sort_field: sortField,
              sort_direction: sortDirection,
              is_default: isDefault,
            });
          }}
          onLoadPreset={(preset) => {
            const pf = preset.filters;
            setStoredFilters(
              conceptId,
              pf,
              (preset.sort_field as SortField | undefined) ?? undefined,
              (preset.sort_direction as SortDirection | undefined) ?? undefined,
              search.wsId,
            );
            navigate({
              search: {
                ...pf,
                sortField: preset.sort_field as SortField | undefined,
                sortDirection: preset.sort_direction as SortDirection | undefined,
              },
            });
          }}
          onDeletePreset={(presetId) => deletePreset.mutate(presetId)}
          onSetDefaultPreset={(presetId, isDefault) =>
            updatePreset.mutate({ presetId, is_default: isDefault })
          }
          hasTensionSort={!!debateMap}
          monitorEnabled={concept?.monitor_enabled}
          untriagedMonitorCount={allPapers.filter((p) => (p.added_source === "monitor" || p.added_source === "trawl_references") && !p.triaged && !p.exclusion_reason).length}
          cullableCount={cullSet.cullIds.length}
          onCullOthers={() => setCullDialogOpen(true)}
          onPurgeDismissed={async () => {
            try {
              const res = await apiClient.post(`api/concepts/${conceptId}/papers/purge-dismissed`).json<{ purged: number; orphans_deleted: number }>();
              await queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
              toast.success(`Purged ${res.purged} dismissed paper${res.purged !== 1 ? "s" : ""}`, {
                description: res.orphans_deleted > 0 ? `${res.orphans_deleted} orphaned record${res.orphans_deleted !== 1 ? "s" : ""} cleaned up` : undefined,
              });
            } catch {
              toast.error("Failed to purge dismissed papers");
            }
          }}
        />
        </div>
      )}

      {/* Seed papers section (always visible for inline management) */}
      <SeedPapersSection
        papers={foundationalPapers}
        selectedPaperId={selectedPaper?.id}
        onSelectPaper={(paper) => setSelectedPaper(paper)}
        conceptId={conceptId}
        hasTrawledPapers={allPapers.length > 0}
        onDiscover={discover}
        loadFailed={foundationalFailed && !foundationalData}
        onRetry={() => void refetchFoundational()}
        angles={angles.multi ? angles.facets : undefined}
        // Hide only while CorpusSeedingPanel (above) is actually rendering its
        // own add/discover/import actions — i.e. the corpus is empty too. A
        // concept with papers but no seeds (e.g. after seeds were demoted)
        // must keep the section's "Discover Seeds" button, or there is no way
        // to run discovery at all in the onboarding phase.
        hideEmptyState={
          guide.phase === "onboarding" &&
          foundationalPapers.length === 0 &&
          allPapers.length === 0 &&
          seedingSkippedFor !== conceptId
        }
      />

      {/* Selection context bar — appears when papers are selected */}
      <SelectionBar
        selectedCount={selectedIds.size}
        totalCount={filtered.length}
        selectedIds={selectedIds}
        onSelectAll={handleSelectAll}
        onDeselectAll={deselectAll}
        onRestoreSelection={(ids) => selectAll(ids)}
        onPromote={handlePromoteSelected}
        onDismiss={() => setBulkDismissOpen(true)}
        onFetchFullText={handleDownloadSelectedPdfs}
        onFlag={() => {
          const ids = Array.from(useSelectionStore.getState().selectedIds);
          ids.forEach((paperId) => {
            inboxActions.createItem.mutate({
              paper_id: paperId,
              source_concept_id: conceptId,
            });
          });
          queryClient.setQueriesData<{ papers: PaperWithRole[] }>(
            { queryKey: ["papers", conceptId] },
            (old) => {
              if (!old) return old;
              const idSet = new Set(ids);
              return {
                ...old,
                papers: old.papers.map((p) =>
                  idSet.has(p.id) ? { ...p, flagged_for_inbox: true } : p,
                ),
              };
            },
          );
          useSelectionStore.getState().deselectAll();
        }}
        workingSets={workingSets}
        onAddToWs={(wsId) => {
          const ids = Array.from(useSelectionStore.getState().selectedIds);
          wsActions.addPapers.mutate({ wsId, paperIds: ids });
          useSelectionStore.getState().deselectAll();
        }}
        onCreateWsFromSelected={(name) => {
          const ids = Array.from(useSelectionStore.getState().selectedIds);
          wsActions.createWorkingSet.mutate({ name }, {
            onSuccess: (ws) => {
              wsActions.addPapers.mutate({ wsId: ws.id, paperIds: ids });
              navigate({ search: { ...search, wsId: ws.id } });
              useSelectionStore.getState().deselectAll();
            },
          });
        }}
      />

      {/* Content: table + optional detail panel + optional evidence map */}
      {(allPapers.length > 0 || trawledLoading || selectedPaper) && (
        <div className="flex gap-4" style={{ height: "calc(100vh - 280px)", minHeight: 300 }}>
          <div className={`min-w-0 overflow-hidden ${selectedPaper ? "flex-[3]" : "flex-1"}`}>
            {search.monitorInboxView ? (
              <MonitorInbox
                angles={angles.multi ? angles.facets : undefined}
                papers={allPapers}
                conceptId={conceptId}
                keyboardEnabled={!pdfMaximized}
                onTriage={(paperId, action) =>
                  triageMutation.mutate(
                    { paperId, action },
                    { onSuccess: () => guideSignals.mark(conceptId, 'hasUsedMonitorTriage') },
                  )
                }
                onTriageBatch={(paperIds, action) =>
                  triageBatchMutation.mutate(
                    { paperIds, action },
                    { onSuccess: () => guideSignals.mark(conceptId, 'hasUsedMonitorTriage') },
                  )
                }
                onDismissWithReason={(paperId, reason, reasonText, findSimilar) => {
                  dismissMutation.mutate({ paperId, reason, reasonText });
                  if (findSimilar) {
                    setFindSimilarModal({ paperId, reason, reasonText });
                    guideSignals.mark(conceptId, 'hasUsedDismissSimilar');
                  }
                }}
                autoDismissThreshold={concept?.monitor_auto_dismiss_threshold ?? null}
                lastMonitorRun={concept?.last_monitor_run}
                nextMonitorRun={concept?.next_monitor_run}
                monitorEnabled={concept?.monitor_enabled ?? false}
                backlogLimit={concept?.monitor_backlog_limit ?? null}
                onInbox={(paperId, mode) => {
                  inboxActions.createItem.mutate(
                    {
                      paper_id: paperId,
                      source_concept_id: conceptId,
                    },
                    { onSuccess: () => guideSignals.mark(conceptId, 'hasUsedResearchInbox') },
                  );
                  if (mode === "move") {
                    triageMutation.mutate({ paperId, action: "dismiss" });
                  }
                  toast.success(mode === "move" ? "Moved to Inbox" : "Copied to Inbox");
                }}
              />
            ) : (
              <>
                <CampScopeBanner
                  label={paperIdScopeLabel}
                  kind={paperIdScopeKind}
                  paperCount={paperIdScope?.length ?? 0}
                  onSaveAsWorkingSet={handleSaveCampScopeAsWorkingSet}
                  onExit={() => clearPaperIdScope(conceptId)}
                />
                {recentlyAddedIds.size > 0 && !search.dismissedView && !search.flaggedView && (
                  <RecentlyAddedChip
                    count={recentlyAddedIds.size}
                    source={raSource}
                    scoringCount={recentlyAddedScoringCount}
                    onSortByNewest={handleSortByNewest}
                    onDismiss={handleDismissRecentlyAdded}
                  />
                )}
                <PaperTable
                  conceptId={conceptId}
                  papers={filtered}
                  loading={trawledLoading}
                  emptyMessage={
                    total > 0
                      ? "No papers match your filters."
                      : "No trawled papers yet. Click Trawl to discover papers."
                  }
                  mode="expand"
                  angles={angles.multi ? angles.facets : undefined}
                  sortResetKey={search.angle ?? ""}
                  seedIds={seedIdSet}
                  onRowClick={setSelectedPaper}
                  onScoreOverride={handleScoreOverride}
                  onReadingStatus={handleReadingStatus}
                  activePaperId={selectedPaper?.id}
                  selectedIds={selectedIds}
                  onSelectionChange={handleSelectionChange}
                  bottomOffset={hasActiveOps ? 40 : 0}
                  recentPaperIds={recentlyAddedIds}
                  scrollTopTick={tableScrollTick}
                  dismissedView={!!search.dismissedView}
                  onRestore={(paperId) => restoreMutation.mutate(paperId)}
                  flaggedView={!!search.flaggedView}
                  onUnflag={(paperId) => {
                    // Optimistic update: clear flagged_for_inbox
                    for (const role of ["foundational", "trawled"] as const) {
                      queryClient.setQueryData<{ papers: PaperWithRole[] }>(
                        ["papers", conceptId, role],
                        (old) => {
                          if (!old) return old;
                          return {
                            ...old,
                            papers: old.papers.map((p) =>
                              p.id === paperId ? { ...p, flagged_for_inbox: false } : p,
                            ),
                          };
                        },
                      );
                    }
                    updatePaperMutation.mutate({ paperId, update: { flagged_for_inbox: false } }, {
                      onSuccess: () => {
                        queryClient.invalidateQueries({ queryKey: ["inbox"] });
                        queryClient.invalidateQueries({ queryKey: ["inbox-count"] });
                      },
                    });
                  }}
                />
              </>
            )}
          </div>

          {selectedPaper && (
            <div className="flex-[2] min-w-0 overflow-hidden">
              {sessionStorage.getItem("paperchase-landscape-return") && (
                <button
                  type="button"
                  onClick={() => {
                    sessionStorage.removeItem("paperchase-landscape-return");
                    sessionStorage.setItem("paperchase-landscape-auto-open", "true");
                    navigate({
                      to: "/concepts/$conceptId/analyze",
                      params: { conceptId },
                    });
                  }}
                  className="mb-1 flex w-full items-center gap-1.5 rounded-md bg-purple-50 px-3 py-1.5 text-xs text-purple-700 hover:bg-purple-100 dark:bg-purple-900/20 dark:text-purple-300 dark:hover:bg-purple-900/30"
                >
                  <ArrowLeft className="size-3" />
                  Back to Landscape
                </button>
              )}
              <PaperDetail
                paper={selectedPaper}
                angles={angles.multi ? angles.facets : undefined}
                conceptId={conceptId}
                paperIndex={paperIndex}
                onClose={() => {
                  setSelectedPaper(null);
                  handleClosePdfViewer();
                }}
                onPromote={handlePromoteSingle}
                onDownloadPdf={handleDownloadSingle}
                onViewPdf={handleViewPdf}
                onExpandPdf={handleExpandPdf}
                onViewFullText={handleViewFullText}
                onExtractAbstract={handleExtractAbstract}
                onSetUpLibraryLink={() => crossNavigate({ to: "/settings/integrations" })}
                onUploadPdf={handleUploadPdf}
                onScoreOverride={handleScoreOverride}
                onSummarize={handleSummarize}
                onRegenerateFromUpload={handleRegenerateFromUpload}
                isSummarizing={summarizeMutation.isPending}
                summarySource={summarySource}
                hasUserUpload={pdfSource === "user"}
                onReadingStatus={handleReadingStatus}
                onNotesChange={handleNotesChange}
                evidenceClips={evidenceClips}
                onViewClip={(page, highlightId) => {
                  const paper = selectedPaperRef.current;
                  if (!paper) return;
                  handleViewPdf(paper.id);
                  if (page != null) setJumpToPage({ page, key: Date.now() });
                  setFocusedHighlightId(highlightId);
                }}
                availableTags={tagsData}
                onUpdateTags={handleUpdatePaperTags}
                onCreateTag={handleCreateTag}
                onUpdateTag={(tagId, data) => {
                  // Optimistically update the selected paper's tags
                  const paper = selectedPaperRef.current;
                  if (paper) {
                    setSelectedPaper({
                      ...paper,
                      tags: paper.tags.map((t) =>
                        t.id === tagId ? { ...t, ...data } : t,
                      ),
                    });
                  }
                  updateTagMutation.mutate({ tagId, ...data });
                }}
                onDismiss={(paperId, reason, reasonText, findSimilar) => {
                  dismissMutation.mutate({ paperId, reason, reasonText });
                  if (findSimilar) {
                    setFindSimilarModal({ paperId, reason, reasonText });
                    guideSignals.mark(conceptId, 'hasUsedDismissSimilar');
                  }
                }}
                findSimilarDisabled={!!findSimilarModal}
                onRelevanceNoteChange={(paperId, note) => updatePaperMutation.mutate({ paperId, update: { relevance_note: note } })}
                onRestore={(paperId) => restoreMutation.mutate(paperId)}
                onFlag={(paperId, note) => {
                  const topSuggestion = crossConceptData?.suggestions?.[0];
                  inboxActions.createItem.mutate(
                    {
                      paper_id: paperId,
                      source_concept_id: conceptId,
                      user_note: note,
                      suggested_concept_id: topSuggestion?.concept_id,
                    },
                    { onSuccess: () => guideSignals.mark(conceptId, 'hasUsedResearchInbox') },
                  );
                  // Optimistically hide the flagged paper
                  queryClient.setQueriesData<{ papers: PaperWithRole[] }>(
                    { queryKey: ["papers", conceptId] },
                    (old) => {
                      if (!old) return old;
                      return {
                        ...old,
                        papers: old.papers.map((p) =>
                          p.id === paperId ? { ...p, flagged_for_inbox: true } : p,
                        ),
                      };
                    },
                  );
                }}
                isRetrieving={isRetrieving}
                isUploading={isUploading}
                viewMode={pdfViewMode}
                explanationContent={
                  explanation ? (
                    <ExplanationCard
                      selectedText={explanation.text}
                      explanation={explanation.result}
                      isLoading={explainMutation.isPending}
                      onDismiss={() => setExplanation(null)}
                    />
                  ) : undefined
                }
                pdfViewerContent={
                  pdfUrl ? (
                    <PdfViewer
                      url={pdfUrl}
                      initialPage={pdfCurrentPage}
                      onPageChange={setPdfCurrentPage}
                      onNumPages={setPdfNumPages}
                      onAppendToAbstract={handleAppendToAbstract}
                      onAddAsChunk={handleAddAsChunk}
                      onExplain={handleExplain}
                      onStashEvidence={handlePdfStashEvidence}
                      paperLabel={pdfPaperLabel}
                      pdfSource={pdfSource}
                      highlights={highlights}
                      selectedIds={selectedHighlightIds}
                      focusedHighlightId={focusedHighlightId}
                      onHighlight={(data) => { createHighlight({ text: data.text, page: data.page, rects: data.rects }); }}
                      onToggleHighlight={toggleHighlight}
                      onDeleteHighlight={deleteHighlight}
                      jumpToPage={jumpToPage}
                    />
                  ) : undefined
                }
                fullTextViewerContent={
                  fullText ? (
                    <FullTextViewer
                      text={fullText}
                      onAppendToAbstract={handleAppendToAbstract}
                      onAddAsChunk={handleAddAsChunk}
                      onExplain={handleExplain}
                      onStashEvidence={handleHtmlStashEvidence}
                      onHighlight={(text, charOffset) => { createHighlight({ text, page: null, rects: charOffset != null ? [{ charOffset }] : null }); }}
                      highlights={highlights}
                      selectedIds={selectedHighlightIds}
                      focusedHighlightId={focusedHighlightId}
                      onToggleHighlight={toggleHighlight}
                    />
                  ) : undefined
                }
                onClosePdfViewer={handleClosePdfViewer}
                onMaximizePdf={() => {
                  setPdfMaximized((v) => !v);
                  // Sync the target viewer to the current page
                  setJumpToPage({ page: pdfCurrentPage - 1, key: Date.now() });
                }}
                pdfMaximized={pdfMaximized}
                onSmartTrawl={() => { smartTrawl.run({ paperId: selectedPaper?.id }); }}
                smartTrawlRunning={smartTrawl.status === "running"}
                hasHighlights={highlights.length > 0}
              />
            </div>
          )}


        </div>
      )}

      {/* Maximized PDF overlay — covers entire viewport */}
      {pdfMaximized && pdfViewMode !== "info" && (pdfUrl || fullText) && selectedPaper && (
        <div className="fixed inset-0 z-50 flex" style={{ backgroundColor: "#1e1e2e" }}>
          {/* Viewer — fills remaining space */}
          <div className="flex-1 flex flex-col min-w-0">
            {pdfViewMode === "fulltext" && fullText ? (
              <FullTextViewer
                text={fullText}
                onAppendToAbstract={handleAppendToAbstract}
                onAddAsChunk={handleAddAsChunk}
                onExplain={handleExplain}
                onStashEvidence={handleHtmlStashEvidence}
                onHighlight={(text, charOffset) => { createHighlight({ text, page: null, rects: charOffset != null ? [{ charOffset }] : null }); }}
                highlights={highlights}
                selectedIds={selectedHighlightIds}
                focusedHighlightId={focusedHighlightId}
                onToggleHighlight={toggleHighlight}
                maximized
              />
            ) : pdfUrl ? (
              <PdfViewer
                url={pdfUrl}
                initialPage={pdfCurrentPage}
                onPageChange={setPdfCurrentPage}
                onNumPages={setPdfNumPages}
                onAppendToAbstract={handleAppendToAbstract}
                onAddAsChunk={handleAddAsChunk}
                onExplain={handleExplain}
                onStashEvidence={handlePdfStashEvidence}
                paperLabel={pdfPaperLabel}
                pdfSource={pdfSource}
                highlights={highlights}
                selectedIds={selectedHighlightIds}
                focusedHighlightId={focusedHighlightId}
                onHighlight={(data) => { createHighlight({ text: data.text, page: data.page, rects: data.rects }); }}
                onToggleHighlight={toggleHighlight}
                onDeleteHighlight={deleteHighlight}
                jumpToPage={jumpToPage}
                continuousScroll
              />
            ) : null}
          </div>
          {/* Highlights sidebar */}
          <div className="w-96 shrink-0 overflow-hidden flex flex-col">
            {/* Sidebar header with minimize/close */}
            <div className="flex items-center justify-between border-b border-surface0 px-3 py-1.5">
              <span className="text-xs font-semibold text-subtext0 uppercase tracking-wider">{pdfViewMode === "fulltext" ? "Full Text Viewer" : "PDF Viewer"}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    setPdfMaximized(false);
                    // Sync compact viewer to the page we were on
                    setJumpToPage({ page: pdfCurrentPage - 1, key: Date.now() });
                  }}
                  className="p-1 rounded hover:bg-surface1 text-subtext0"
                  title="Restore split view"
                >
                  <Minimize2 className="size-3.5" />
                </button>
                <button
                  onClick={() => {
                    setPdfMaximized(false);
                    handleClosePdfViewer();
                  }}
                  className="p-1 rounded hover:bg-surface1 text-subtext0"
                  title="Back to Info"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              <HighlightsSidebar
                highlights={highlights}
                selectedIds={selectedHighlightIds}
                onToggle={toggleHighlight}
                onSelectAll={selectAllHighlights}
                onClearAll={clearAllHighlights}
                onDelete={deleteHighlight}
                onDeleteAll={() => highlights.forEach((h) => deleteHighlight(h.id))}
                onSmartTrawl={() => { smartTrawl.run({ paperId: selectedPaper?.id }); }}
                smartTrawlStatus={smartTrawl.status}
                onJumpToPage={(page, highlightId) => {
                  setJumpToPage({ page, key: Date.now() });
                  setFocusedHighlightId(highlightId ?? null);
                }}
                onScrollToHighlight={(highlightId) => setFocusedHighlightId(highlightId)}
                onStashEvidence={handleBulkStashEvidence}
                annotationImport={annotationImport}
                hasPdf={!!selectedPaper?.canonical_pdf_storage_key}
              />
            </div>
          </div>
        </div>
      )}

      {findSimilarModal && (
        <SimilarPapersModal
          conceptId={conceptId}
          paperId={findSimilarModal.paperId}
          reason={findSimilarModal.reason}
          reasonText={findSimilarModal.reasonText}
          onClose={() => setFindSimilarModal(null)}
          onDismissed={(paperIds) => {
            bulkDismissMutation.mutate(
              {
                paperIds,
                reason: findSimilarModal.reason,
                reasonText: findSimilarModal.reasonText,
              },
              { onSuccess: () => guideSignals.mark(conceptId, 'hasUsedDismissSimilar') },
            );
            setFindSimilarModal(null);
          }}
        />
      )}

      <BulkDismissDialog
        open={bulkDismissOpen}
        onOpenChange={setBulkDismissOpen}
        count={selectedIds.size}
        onConfirm={(reason, reasonText) => {
          const ids = Array.from(useSelectionStore.getState().selectedIds);
          bulkDismissMutation.mutate({ paperIds: ids, reason, reasonText });
        }}
      />

      <CullOthersDialog
        open={cullDialogOpen}
        onOpenChange={setCullDialogOpen}
        count={cullSet.cullIds.length}
        filterSummary={describeFilters(filters)}
        protectedChampioned={cullSet.protectedChampioned}
        protectedUntriaged={cullSet.protectedUntriaged}
        onConfirm={() => {
          bulkDismissMutation.mutate({
            paperIds: cullSet.cullIds,
            reason: "cull",
            reasonText: describeFilters(filters),
          });
        }}
      />

      <StartOverDialog
        open={showStartOver}
        onClose={() => setShowStartOver(false)}
        conceptId={conceptId}
        scope="all"
        onDone={() => discover()}
      />

      <BulkPdfImportDialog
        open={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        papers={allPapers}
        conceptId={conceptId}
      />

      {/* Per-file attestation dialog for single-file PDF uploads. Backend
          /confirm-upload requires rights_attestation=true; bulk import has
          its own checkbox, but the single-file upload button did not — this
          prompts the user for explicit consent before the upload proceeds. */}
      <Dialog
        open={pendingUpload !== null}
        onOpenChange={(open) => {
          if (!open) cancelAttestedUpload();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload private PDF copy</DialogTitle>
            <DialogDescription>
              Your uploaded PDF will stay private to your account. It is never
              shown to other users.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={uploadAttested}
              onChange={(e) => setUploadAttested(e.target.checked)}
              className="mt-0.5 size-3.5 shrink-0"
            />
            <span>
              I confirm I have the right to process this PDF under my own
              research access. My uploaded copy will stay private to my account
              and will not be shared with other users.
            </span>
          </label>
          <DialogFooter className="items-center">
            {!uploadAttested && (
              <ReasonLine id="private-upload-why" className="mr-auto">Tick the box above to upload.</ReasonLine>
            )}
            <Button variant="outline" onClick={cancelAttestedUpload}>
              Cancel
            </Button>
            <Button
              onClick={confirmAttestedUpload}
              disabled={!uploadAttested}
              aria-describedby={!uploadAttested ? "private-upload-why" : undefined}
            >
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {seedDiscoveryCandidates && <SeedDiscoveryDialog conceptId={conceptId} />}
      {/* Bound to the concept it was searched from: a route change to another
          concept must not add these results there (Codex review LOW). */}
      {searchPreview && searchPreview.conceptId === conceptId && (
        <SearchResultsDialog
          key={searchPreview.seq}
          conceptId={searchPreview.conceptId}
          query={searchPreview.query}
          response={searchPreview.response}
          onClose={() => setSearchPreview(null)}
        />
      )}

      <OperationBar
        onCancelPdf={cancelRetrieval}
        onCancelScoring={cancelScoring}
        onCancelSnowball={() => useSnowballStore.getState().cancelSnowball()}
        onCancelMonitor={cancelMonitor}
        conceptId={conceptId}
      />
      <OperationBarCallout />

    </div>
  );
}
